import { useState, useEffect, useCallback } from "react";

const API = "https://scraps-backend.onrender.com";
const FONT = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&family=Inter:wght@400;500;600;700&display=swap');`;
const CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', sans-serif; background: #0a0e1a; }
  @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
  @keyframes popIn { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }
  ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
  @media (max-width: 767px) { .desktop-only { display: none !important; } }
`;

const fmt = (price) => price > 0 ? `+${price}` : `${price}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

// ─── MODAL ────────────────────────────────────────────────────────────────────
function Modal({ onClose, children }) {
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, backgroundColor:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ backgroundColor:"#ffffff", borderRadius:16, padding:28, maxWidth:400, width:"100%", animation:"popIn 0.2s", boxShadow:"0 20px 60px rgba(0,0,0,0.4)" }}>
        {children}
      </div>
    </div>
  );
}

// ─── ODDS BUTTON ──────────────────────────────────────────────────────────────
function OddsBtn({ label, sublabel, selected, onClick, color="#3d9e7a" }) {
  const bgMap = { "#4d9fff":"#eff6ff", "#f5b731":"#fffbeb", "#3d9e7a":"#f0fdf8" };
  const selBgMap = { "#4d9fff":"#bfdbfe", "#f5b731":"#fde68a", "#3d9e7a":"#a7f3d0" };
  const bg = selected ? selBgMap[color]||"#d1fae5" : bgMap[color]||"#f9fafb";
  const borderColor = selected ? color : `${color}50`;
  return (
    <button onClick={onClick} style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"6px 4px", minWidth:48, border:`1.5px solid ${borderColor}`, borderRadius:6, cursor:"pointer", backgroundColor:bg, transition:"all 0.12s", width:"100%" }}>
      <span style={{ fontSize:10, color:"#1a1a2e", fontWeight:500, fontFamily:"'Inter',sans-serif" }}>{sublabel}</span>
      <span style={{ fontSize:12, fontWeight:700, color:"#1a1a2e", fontFamily:"'Inter',sans-serif" }}>{label}</span>
    </button>
  );
}

// ─── GAME CARD ────────────────────────────────────────────────────────────────
function GameCard({ game, slip, onAddBet }) {
  const getMarket = (g, key) => g.bookmakers?.[0]?.markets?.find(m => m.key === key);
  const spread = getMarket(game, "spreads");
  const total = getMarket(game, "totals");
  const ml = getMarket(game, "h2h");
  const isSel = (gid, mkt, name) => slip.some(b => b.gameId === gid && b.market === mkt && b.selection === name);

  const handle = (market, outcome, marketLabel, icon) => {
    if (isSel(game.id, market, outcome.name)) onAddBet(null, game.id, market);
    else onAddBet({ gameId:game.id, market, marketLabel, icon, selection:outcome.name, price:outcome.price, point:outcome.point, gameName:`${game.away_team} @ ${game.home_team}`, sport:game.sport, commence_time:game.commence_time }, game.id, market);
  };

  const formatTime = (t) => new Date(t).toLocaleString("en-US", { weekday:"short", month:"numeric", day:"numeric", hour:"numeric", minute:"2-digit", timeZone:"America/New_York" }) + " ET";

  return (
    <div style={{ backgroundColor:"#ffffff", borderRadius:12, marginBottom:10, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.12)", animation:"fadeIn 0.3s" }}>
      <div style={{ padding:"8px 14px", backgroundColor:"#f8fafc", borderBottom:"1px solid #e5e7eb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <span style={{ fontSize:11, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>{formatTime(game.commence_time)}</span>
        <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>DraftKings</span>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 90px 90px 56px", padding:"6px 10px 2px" }}>
        <div/>{["SPREAD","TOTAL","ML"].map(h=><div key={h} style={{ textAlign:"center", fontSize:10, color:"#9ca3af", letterSpacing:"0.08em", fontFamily:"'Inter',sans-serif", fontWeight:600 }}>{h}</div>)}
      </div>
      {[game.away_team, game.home_team].map((team, idx) => {
        const spd = spread?.outcomes.find(o => o.name === team);
        const tot = total?.outcomes[idx];
        const mlo = ml?.outcomes.find(o => o.name === team);
        return (
          <div key={team} style={{ display:"grid", gridTemplateColumns:"1fr 90px 90px 56px", gap:4, padding:"8px 10px", alignItems:"center", borderTop: idx === 0 ? "none" : "1px solid #f3f4f6" }}>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:"#1a1a2e", fontFamily:"'Inter',sans-serif", lineHeight:1.2 }}>{team}</div>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>{idx===0?"Away":"Home"}</div>
            </div>
            <div style={{ display:"flex", justifyContent:"center" }}>
              {spd && <OddsBtn sublabel={spd.point>0?`+${spd.point}`:`${spd.point}`} label={fmt(spd.price)} selected={isSel(game.id,"spreads",team)} onClick={()=>handle("spreads",{name:team,price:spd.price,point:spd.point},"Spread","⟺")} color="#4d9fff"/>}
            </div>
            <div style={{ display:"flex", justifyContent:"center" }}>
              {tot && <OddsBtn sublabel={`${tot.name} ${tot.point}`} label={fmt(tot.price)} selected={isSel(game.id,"totals",tot.name)} onClick={()=>handle("totals",{name:tot.name,price:tot.price,point:tot.point},"Total","≈")} color="#f5b731"/>}
            </div>
            <div style={{ display:"flex", justifyContent:"center" }}>
              {mlo && <OddsBtn sublabel="" label={fmt(mlo.price)} selected={isSel(game.id,"h2h",team)} onClick={()=>handle("h2h",{name:team,price:mlo.price},"Moneyline","✓")} color="#3d9e7a"/>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── BET SLIP ─────────────────────────────────────────────────────────────────
function BetSlip({ slip, onRemove, credits, onBetPlaced, leagueId, teamId, periodId, matchupId, activeSport }) {
  const [wager, setWager] = useState(100);
  const [wagerInput, setWagerInput] = useState("100");
  const [placing, setPlacing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [betMode, setBetMode] = useState("auto");
  const [teaserPts, setTeaserPts] = useState(6);

  const isFootball = activeSport && (activeSport.includes("nfl") || activeSport.includes("ncaaf") || activeSport.includes("college"));
  const canTeaser = isFootball && slip.length >= 2 && slip.length <= 6 && slip.every(b=>b.market==="spreads"||b.market==="totals");
  const isTeaser = betMode==="teaser" && canTeaser;
  const isParlay = !isTeaser && slip.length > 1;

  const TEASER_ODDS = {
    2:{6:100,6.5:-110,7:-120}, 3:{6:180,6.5:160,7:140},
    4:{6:300,6.5:260,7:240}, 5:{6:450,6.5:400,7:350}, 6:{6:700,6.5:600,7:500},
  };
  const teaserLegs = Math.min(slip.length, 6);
  const teaserOdds = (TEASER_ODDS[teaserLegs]||TEASER_ODDS[6])[teaserPts]||100;
  const teaserDec = (o) => o < 0 ? 1+(100/Math.abs(o)) : 1+(o/100);
  const teaserPayout = Math.round(wager*(teaserDec(teaserOdds)-1));

  const adjustLine = (bet, pts) => {
    if (bet.market==="spreads") {
      // Point is already signed relative to the selected team (negative =
      // favorite, positive = underdog), so adding points always helps:
      // -7 + 6 = -1, +3 + 6 = +9.
      const base = parseFloat(bet.point)||0;
      return base + pts;
    }
    if (bet.market==="totals") {
      const base = parseFloat(bet.point)||0;
      return (bet.selection||"").toLowerCase().includes("over") ? base - pts : base + pts;
    }
    return bet.point;
  };

  const calcParlay = () => {
    let decimal = 1;
    slip.forEach(b => { decimal *= b.price > 0 ? (b.price/100)+1 : 1+(100/Math.abs(b.price)); });
    const american = decimal >= 2 ? Math.round((decimal-1)*100) : Math.round(-100/(decimal-1));
    return { odds: american, payout: Math.round(wager*(decimal-1)) };
  };
  const parlay = isParlay ? calcParlay() : null;
  const singlePayout = !isParlay && !isTeaser && slip[0] ? Math.round(slip[0].price > 0 ? wager*(slip[0].price/100) : wager*(100/Math.abs(slip[0].price))) : 0;
  const payout = isTeaser ? teaserPayout : isParlay ? parlay.payout : singlePayout;

  const doPlace = async () => {
    setPlacing(true);
    try {
      let body;
      if (isTeaser) {
        const teaserLegsData = slip.map(b => ({
          ...b, originalPoint: b.point, point: adjustLine(b, teaserPts), teaserPoints: teaserPts,
        }));
        body = { team_id:teamId, game_id:slip.map(b=>b.gameId).join("_"), game_name:`${slip.length}-Leg Teaser`, sport:slip[0].sport||"", selection:teaserLegsData.map(b=>`${b.selection} ${b.point>0?"+":""}${b.point}`).join(" + "), market:"teaser", market_label:"Teaser", price:teaserOdds, wager, to_win:teaserPayout, is_parlay:true, parlay_legs:teaserLegsData, teaser_points:teaserPts, commence_time:slip[0].commence_time };
      } else if (isParlay) {
        body = { team_id:teamId, game_id:slip[0].gameId, game_name:slip.map(b=>b.gameName).join(" | "), sport:slip[0].sport||"", selection:`${slip.length}-Leg Parlay`, market:"parlay", market_label:"Parlay", price:parlay.odds, wager, to_win:parlay.payout, is_parlay:true, parlay_legs:slip, commence_time:slip[0].commence_time };
      } else {
        const b = slip[0];
        body = { team_id:teamId, game_id:b.gameId, game_name:b.gameName, sport:b.sport||"", selection:b.selection, market:b.market, market_label:b.marketLabel, price:b.price, point:b.point, wager, to_win:singlePayout, is_parlay:false, parlay_legs:[], commence_time:b.commence_time };
      }
      const res = await fetch(`${API}/h2h/${leagueId}/bet`, {
        method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      setPlaced(true);
      setConfirmOpen(false);
      onBetPlaced();
      setTimeout(() => setPlaced(false), 3000);
    } catch(e) { alert("Error: " + e.message); }
    setPlacing(false);
  };

  if (slip.length === 0) return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, textAlign:"center", backgroundColor:"#ffffff" }}>
      <div style={{ fontSize:32, marginBottom:12, opacity:0.3 }}>🎟</div>
      <div style={{ fontFamily:"'Inter',sans-serif", fontSize:14, color:"#9ca3af", fontWeight:500 }}>Select odds to build your bet</div>
      <div style={{ fontSize:12, color:"#9ca3af", marginTop:6 }}>Add 1 for a single · Add 2+ for a parlay or teaser</div>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", backgroundColor:"#ffffff", overflow:"hidden" }}>
      {confirmOpen && (
        <Modal onClose={()=>setConfirmOpen(false)}>
          <div style={{ textAlign:"center", marginBottom:20 }}>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:20, fontWeight:800, color:"#1a1a2e", marginBottom:6 }}>Confirm Bet</div>
            <div style={{ display:"inline-block", fontSize:12, fontWeight:600, padding:"3px 12px", borderRadius:20, backgroundColor:isTeaser?"#eff6ff":isParlay?"#fff7ed":"#f0fdf8", color:isTeaser?"#1d4ed8":isParlay?"#92400e":"#065f46", border:isTeaser?"1px solid #93c5fd":isParlay?"1px solid #fcd34d":"1px solid #a7f3d0", fontFamily:"'Inter',sans-serif" }}>
              {isTeaser?`🎯 ${slip.length}-Leg Teaser (+${teaserPts} pts)`:isParlay?`⚡ ${slip.length}-Leg Parlay`:"Single Bet"}
            </div>
          </div>
          <div style={{ backgroundColor:"#f9fafb", borderRadius:10, padding:"14px 16px", marginBottom:12, border:"1px solid #e5e7eb" }}>
            {slip.map((b,i)=><div key={i} style={{ display:"flex", justifyContent:"space-between", marginBottom:i<slip.length-1?8:0 }}>
              <span style={{ fontSize:14, color:"#1a1a2e", fontFamily:"'Inter',sans-serif", fontWeight:500 }}>
                {b.selection}{isTeaser ? ` ${adjustLine(b,teaserPts)>0?"+":""}${adjustLine(b,teaserPts)} (was ${b.point>0?"+":""}${b.point})` : b.point?` ${b.point>0?"+":""}${b.point}`:""}
              </span>
              <span style={{ fontSize:13, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>{isTeaser?fmt(teaserOdds):fmt(b.price)}</span>
            </div>)}
          </div>
          <div style={{ backgroundColor:"#ffffff", borderRadius:10, padding:"14px 16px", marginBottom:20, border:"1px solid #e5e7eb" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
              <span style={{ fontSize:13, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>Wager</span>
              <span style={{ fontSize:14, fontWeight:600, color:"#1a1a2e", fontFamily:"'Inter',sans-serif" }}>{fmtNum(wager)}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", paddingTop:10, borderTop:"1px solid #e5e7eb" }}>
              <span style={{ fontSize:14, fontWeight:700, color:"#1a1a2e", fontFamily:"'Inter',sans-serif" }}>To win</span>
              <span style={{ fontSize:20, fontWeight:800, color:"#3d9e7a", fontFamily:"'Inter',sans-serif" }}>+{fmtNum(payout)}</span>
            </div>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={doPlace} disabled={placing} style={{ flex:1, padding:13, backgroundColor:"#3d9e7a", color:"#ffffff", border:"none", borderRadius:8, fontFamily:"'Inter',sans-serif", fontSize:15, fontWeight:600, cursor:"pointer" }}>
              {placing ? "Placing..." : "Confirm Bet"}
            </button>
            <button onClick={()=>setConfirmOpen(false)} style={{ flex:1, padding:13, backgroundColor:"transparent", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:8, cursor:"pointer", fontSize:14, fontFamily:"'Inter',sans-serif" }}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* Mode selector */}
      {slip.length >= 2 && (
        <div style={{ margin:"12px 20px 0", display:"flex", gap:6 }}>
          <button onClick={()=>setBetMode("auto")} style={{ flex:1, padding:"7px 0", fontSize:12, fontWeight:700, fontFamily:"'Inter',sans-serif", border:"none", borderRadius:6, cursor:"pointer", background:!isTeaser?"#1a2235":"#f3f4f6", color:!isTeaser?"#ffffff":"#6b7280" }}>
            Parlay
          </button>
          {canTeaser && <button onClick={()=>setBetMode("teaser")} style={{ flex:1, padding:"7px 0", fontSize:12, fontWeight:700, fontFamily:"'Inter',sans-serif", border:"none", borderRadius:6, cursor:"pointer", background:isTeaser?"#1d4ed8":"#f3f4f6", color:isTeaser?"#ffffff":"#6b7280" }}>
            🎯 Teaser
          </button>}
        </div>
      )}

      {/* Teaser point selector */}
      {isTeaser && (
        <div style={{ margin:"8px 20px 0", display:"flex", gap:6 }}>
          {[6, 6.5, 7].map(pts=>(
            <button key={pts} onClick={()=>setTeaserPts(pts)} style={{ flex:1, padding:"6px 0", fontSize:12, fontWeight:700, fontFamily:"'Inter',sans-serif", border:`1.5px solid ${teaserPts===pts?"#1d4ed8":"#e5e7eb"}`, borderRadius:6, cursor:"pointer", background:teaserPts===pts?"#eff6ff":"#ffffff", color:teaserPts===pts?"#1d4ed8":"#6b7280" }}>
              +{pts} pts
            </button>
          ))}
        </div>
      )}

      <div style={{ margin:"8px 20px 4px", padding:"10px 14px", backgroundColor:isTeaser?"#eff6ff":isParlay?"#fff7ed":"#f0fdf8", border:isTeaser?"1px solid #93c5fd":isParlay?"1px solid #fcd34d":"1px solid #a7f3d0", borderRadius:8, display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:600, color:isTeaser?"#1d4ed8":isParlay?"#92400e":"#065f46" }}>
          {isTeaser?`🎯 ${slip.length}-Leg Teaser (+${teaserPts} pts)`:isParlay?`⚡ ${slip.length}-Leg Parlay`:"Single Bet"}
        </span>
        {isTeaser && <span style={{ fontFamily:"'Inter',sans-serif", fontSize:14, fontWeight:700, color:"#1d4ed8" }}>{fmt(teaserOdds)}</span>}
        {isParlay && <span style={{ fontFamily:"'Inter',sans-serif", fontSize:14, fontWeight:700, color:"#ff4d6d" }}>{fmt(parlay.odds)}</span>}
      </div>

      <div style={{ overflowY:"auto", flex:1, minHeight:0, padding:"12px 20px", display:"flex", flexDirection:"column", gap:10, backgroundColor:"#ffffff" }}>
        {slip.map(bet => {
          const adjPt = isTeaser ? adjustLine(bet, teaserPts) : bet.point;
          return (
            <div key={`${bet.gameId}-${bet.market}`} style={{ backgroundColor:"#1a2235", borderRadius:10, padding:"12px 14px", borderLeft:`4px solid ${isTeaser?"#1d4ed8":bet.market==="spreads"?"#4d9fff":bet.market==="totals"?"#f5b731":"#3d9e7a"}`, position:"relative" }}>
              <button onClick={()=>onRemove(bet.gameId,bet.market)} style={{ position:"absolute", right:8, top:8, background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer", fontSize:14 }}>✕</button>
              <div style={{ fontSize:15, fontWeight:700, color:"#ffffff", fontFamily:"'Inter',sans-serif", marginBottom:4, paddingRight:20 }}>
                {bet.selection}{adjPt!==null&&adjPt!==undefined?` ${adjPt>0?`+${adjPt}`:adjPt}`:""}
                {isTeaser&&<span style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginLeft:6 }}>(was {bet.point>0?`+${bet.point}`:bet.point})</span>}
              </div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)", fontFamily:"'Inter',sans-serif", marginBottom:6 }}>{bet.gameName}</div>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:10, padding:"2px 7px", borderRadius:4, backgroundColor:isTeaser?"rgba(29,78,216,0.2)":bet.market==="spreads"?"rgba(77,159,255,0.15)":bet.market==="totals"?"rgba(245,183,49,0.15)":"rgba(61,158,122,0.15)", color:isTeaser?"#93c5fd":bet.market==="spreads"?"#4d9fff":bet.market==="totals"?"#f5b731":"#3d9e7a", fontFamily:"'Inter',sans-serif", fontWeight:600 }}>{isTeaser?"Teaser":bet.marketLabel}</span>
                <span style={{ fontSize:12, color:"rgba(255,255,255,0.6)", fontFamily:"'Inter',sans-serif" }}>{isTeaser?fmt(teaserOdds):fmt(bet.price)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding:"16px 20px 20px", backgroundColor:"#f9fafb", borderTop:"1px solid #e5e7eb" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#374151", letterSpacing:"0.08em", fontFamily:"'Inter',sans-serif" }}>WAGER</div>
          <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>/ {fmtNum(credits)} cr</div>
        </div>
        <input type="number" value={wagerInput} min={1} max={credits} onChange={e=>{ setWagerInput(e.target.value); const v=Number(e.target.value); if(v>=1) setWager(Math.min(v,credits)); }} onBlur={()=>{ if(!wagerInput||Number(wagerInput)<1){ setWagerInput("1"); setWager(1); } }} style={{ width:"100%", padding:"10px 14px", backgroundColor:"#ffffff", border:"1.5px solid #3d9e7a", borderRadius:8, color:"#1a1a2e", fontSize:18, fontFamily:"'Inter',sans-serif", fontWeight:700, outline:"none", boxSizing:"border-box", marginBottom:10, textAlign:"center" }}/>
        <div style={{ display:"flex", gap:6, marginBottom:14 }}>
          {[50,100,250,500].map(v=>(
            <button key={v} onClick={()=>{ setWager(Math.min(v,credits??1000)); setWagerInput(String(Math.min(v,credits??1000))); }} style={{ flex:1, padding:"7px 0", backgroundColor:wager===Math.min(v,credits??1000)?"#3d9e7a":"#f9fafb", border:wager===Math.min(v,credits??1000)?"1.5px solid #3d9e7a":"1px solid #e5e7eb", borderRadius:6, color:wager===Math.min(v,credits??1000)?"#ffffff":"#374151", cursor:"pointer", fontSize:12, fontFamily:"'Inter',sans-serif", fontWeight:600 }}>{v}</button>
          ))}
        </div>
        <div style={{ backgroundColor:"#ffffff", borderRadius:8, padding:"12px 14px", border:"1px solid #e5e7eb", marginBottom:14 }}>
          {isTeaser && <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
            <span style={{ fontSize:12, color:"#374151", fontFamily:"'Inter',sans-serif" }}>Teaser odds</span>
            <span style={{ fontSize:13, fontWeight:700, color:"#1d4ed8", fontFamily:"'Inter',sans-serif" }}>{fmt(teaserOdds)}</span>
          </div>}
          {isParlay && <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
            <span style={{ fontSize:12, color:"#374151", fontFamily:"'Inter',sans-serif" }}>Parlay odds</span>
            <span style={{ fontSize:13, fontWeight:700, color:"#ff4d6d", fontFamily:"'Inter',sans-serif" }}>{fmt(parlay.odds)}</span>
          </div>}
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
            <span style={{ fontSize:12, color:"#374151", fontFamily:"'Inter',sans-serif" }}>Wager</span>
            <span style={{ fontSize:13, color:"#1a1a2e", fontFamily:"'Inter',sans-serif", fontWeight:500 }}>{fmtNum(wager)}</span>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", paddingTop:8, borderTop:"1px solid #e5e7eb" }}>
            <span style={{ fontSize:14, fontWeight:700, color:"#1a1a2e", fontFamily:"'Inter',sans-serif" }}>To win</span>
            <span style={{ fontSize:18, fontWeight:800, color:"#3d9e7a", fontFamily:"'Inter',sans-serif" }}>+{fmtNum(payout)}</span>
          </div>
        </div>
        {placed ? (
          <div style={{ padding:"14px", backgroundColor:"#f0fdf8", border:"1px solid #a7f3d0", borderRadius:8, textAlign:"center", color:"#3d9e7a", fontFamily:"'Inter',sans-serif", fontSize:14, fontWeight:600 }}>
            ✓ Bet placed!
          </div>
        ) : (
          <button onClick={()=>setConfirmOpen(true)} disabled={placing||wager>credits} style={{ width:"100%", padding:"13px", border:"none", backgroundColor:wager>credits?"#e5e7eb":"#3d9e7a", color:wager>credits?"#9ca3af":"#ffffff", borderRadius:8, cursor:wager>credits?"not-allowed":"pointer", fontFamily:"'Inter',sans-serif", fontSize:15, fontWeight:600 }}>
            {placing?"Placing...":`Place ${isTeaser?"Teaser":isParlay?"Parlay":"Bet"} · ${fmtNum(wager)}`}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── STANDINGS VIEW ────────────────────────────────────────────────────────────
function StandingsView({ leagueId, teamId }) {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const mob = window.innerWidth < 768;

  useEffect(() => {
    fetch(`${API}/h2h/${leagueId}/standings`).then(r=>r.json()).then(d=>{setStandings(d);setLoading(false);});
  }, [leagueId]);

  if (loading) return <div style={{ textAlign:"center", padding:60, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>Loading standings...</div>;

  return (
    <div style={{ padding:mob?"12px":"16px 20px", maxWidth:860, margin:"0 auto" }}>
      {/* Section header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, paddingBottom:12, borderBottom:"2px solid rgba(255,255,255,0.08)" }}>
        <div style={{ fontFamily:"'Inter',sans-serif", fontSize:20, fontWeight:800, color:"#f0f4ff" }}>Standings</div>
        <div style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:20, padding:"4px 12px", fontSize:13, fontWeight:600, color:"#f0f4ff", fontFamily:"'Inter',sans-serif" }}>{standings.length} teams</div>
      </div>
      {/* Column headers */}
      {!mob && (
        <div style={{ display:"grid", gridTemplateColumns:"44px 1fr 150px 110px", gap:8, alignItems:"center", padding:"0 16px 10px" }}>
          <div style={{ fontSize:11, fontWeight:600, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>#</div>
          <div style={{ fontSize:11, fontWeight:600, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>TEAM</div>
          <div style={{ fontSize:11, fontWeight:600, color:"#9ca3af", fontFamily:"'Inter',sans-serif", textAlign:"center" }}>W - L - T</div>
          <div style={{ fontSize:11, fontWeight:600, color:"#9ca3af", fontFamily:"'Inter',sans-serif", textAlign:"right" }}>GAINED +/-</div>
        </div>
      )}
      {mob && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:8, padding:"0 4px 8px" }}>
          <div style={{ fontSize:11, fontWeight:600, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>TEAM</div>
          <div style={{ fontSize:11, fontWeight:600, color:"#9ca3af", fontFamily:"'Inter',sans-serif", textAlign:"right" }}>RECORD · GAINED</div>
        </div>
      )}
      {standings.map((s, i) => {
        const isMe = s.team_id === teamId;
        return mob ? (
          <div key={s.team_id} style={{ backgroundColor:isMe?"#f0fdf8":"#ffffff", border:isMe?"1.5px solid #a7f3d0":"1px solid #f3f4f6", borderRadius:12, padding:"12px 14px", marginBottom:8, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:20, fontWeight:800, flexShrink:0 }}>
                {i===0?"🥇":i===1?"🥈":i===2?"🥉":<span style={{ color:"#9ca3af", fontSize:14, fontFamily:"'Inter',sans-serif" }}>#{i+1}</span>}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontFamily:"'Inter',sans-serif", fontSize:14, fontWeight:700, color:"#1a1a2e" }}>{s.team_name}</span>
                  {isMe && <span style={{ fontSize:9, padding:"2px 6px", backgroundColor:"#f0fdf8", color:"#3d9e7a", border:"1px solid #a7f3d0", borderRadius:4, fontWeight:700 }}>YOU</span>}
                </div>
                <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>{s.owner_name}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ display:"flex", alignItems:"center", gap:2 }}>
                  <span style={{ fontFamily:"'Inter',sans-serif", fontSize:18, fontWeight:800, color:"#111827" }}>{s.wins}</span>
                  <span style={{ fontSize:11, fontWeight:700, color:"#3d9e7a" }}>W</span>
                  <span style={{ fontSize:14, color:"#9ca3af", margin:"0 2px" }}>-</span>
                  <span style={{ fontFamily:"'Inter',sans-serif", fontSize:18, fontWeight:800, color:"#111827" }}>{s.losses}</span>
                  <span style={{ fontSize:11, fontWeight:700, color:"#dc2626" }}>L</span>
                  {s.ties > 0 && <>
                    <span style={{ fontSize:14, color:"#9ca3af", margin:"0 2px" }}>-</span>
                    <span style={{ fontFamily:"'Inter',sans-serif", fontSize:18, fontWeight:800, color:"#111827" }}>{s.ties}</span>
                    <span style={{ fontSize:11, fontWeight:700, color:"#6b7280" }}>T</span>
                  </>}
                </div>
                <div style={{ fontSize:12, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>{fmtNum(s.total_credits_gained)}</div>
              </div>
            </div>
            {s.is_champion && <div style={{ textAlign:"center", padding:"6px", backgroundColor:"#fffbeb", border:"1px solid #fcd34d", borderRadius:6, fontSize:12, fontWeight:700, color:"#92400e" }}>🏆 Champion!</div>}
          </div>
        ) : (
          <div key={s.team_id} style={{ display:"grid", gridTemplateColumns:"44px 1fr 150px 110px", gap:8, alignItems:"center", padding:"14px 16px", marginBottom:8, backgroundColor:isMe?"#f0fdf8":"#ffffff", border:isMe?"1.5px solid #a7f3d0":"1px solid #f3f4f6", borderRadius:12, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:20, fontWeight:800 }}>
              {i===0?"🥇":i===1?"🥈":i===2?"🥉":<span style={{ color:"#9ca3af", fontSize:14, fontFamily:"'Inter',sans-serif" }}>#{i+1}</span>}
            </div>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                <span style={{ fontFamily:"'Inter',sans-serif", fontSize:14, fontWeight:700, color:"#1a1a2e" }}>{s.team_name}</span>
                {isMe && <span style={{ fontSize:9, padding:"2px 6px", backgroundColor:"#f0fdf8", color:"#3d9e7a", border:"1px solid #a7f3d0", borderRadius:4, fontWeight:700 }}>YOU</span>}
                {s.is_champion && <span style={{ fontSize:9, padding:"2px 6px", backgroundColor:"#fffbeb", color:"#92400e", border:"1px solid #fcd34d", borderRadius:4, fontWeight:700 }}>🏆 CHAMPION</span>}
              </div>
              <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>{s.owner_name}</div>
            </div>
            <div style={{ textAlign:"center", display:"flex", alignItems:"center", justifyContent:"center", gap:2 }}>
              <span style={{ fontFamily:"'Inter',sans-serif", fontSize:18, fontWeight:800, color:"#111827" }}>{s.wins}</span>
              <span style={{ fontSize:11, fontWeight:700, color:"#3d9e7a" }}>W</span>
              <span style={{ fontSize:14, color:"#9ca3af", margin:"0 2px" }}>-</span>
              <span style={{ fontFamily:"'Inter',sans-serif", fontSize:18, fontWeight:800, color:"#111827" }}>{s.losses}</span>
              <span style={{ fontSize:11, fontWeight:700, color:"#dc2626" }}>L</span>
              {s.ties > 0 && <>
                <span style={{ fontSize:14, color:"#9ca3af", margin:"0 2px" }}>-</span>
                <span style={{ fontFamily:"'Inter',sans-serif", fontSize:18, fontWeight:800, color:"#111827" }}>{s.ties}</span>
                <span style={{ fontSize:11, fontWeight:700, color:"#6b7280" }}>T</span>
              </>}
            </div>
            <div style={{ textAlign:"right", fontFamily:"'Inter',sans-serif", fontSize:16, fontWeight:800, color:"#111827" }}>{fmtNum(s.total_credits_gained)}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── MATCHUPS VIEW ─────────────────────────────────────────────────────────────
function MatchupsView({ leagueId, teamId, periodId, period, startingCredits, allPeriods }) {
  const [matchups, setMatchups] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [selectedPeriodId, setSelectedPeriodId] = useState(periodId);
  const [selectedPeriod, setSelectedPeriod] = useState(period);

  const toggleExpanded = (id) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  useEffect(() => {
    setSelectedPeriodId(periodId);
    setSelectedPeriod(period);
  }, [periodId, period]);

  const loadMatchups = () => {
    if (!selectedPeriodId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      fetch(`${API}/h2h/${leagueId}/matchups/${selectedPeriodId}?viewer_team_id=${teamId}`).then(r=>r.ok?r.json():[]),
      fetch(`${API}/h2h/${leagueId}/standings`).then(r=>r.ok?r.json():[]),
    ]).then(([m, s]) => {
      setMatchups(Array.isArray(m)?m:[]);
      setStandings(Array.isArray(s)?s:[]);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    loadMatchups();
  }, [leagueId, selectedPeriodId]);



  const getRecord = (tid, showTies=false) => {
    const s = standings.find(x => x.team_id === tid);
    if (!s) return "(0-0)";
    if (showTies && s.ties > 0) return `(${s.wins}-${s.losses}-${s.ties})`;
    return `(${s.wins}-${s.losses})`;
  };

  const sc = startingCredits || 1000;
  const creditColor = (cr) => cr > sc ? "#3d9e7a" : cr < sc ? "#ef4444" : "#111827";

  const formatDateRange = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    // end_date is exclusive so subtract 1 day for display
    e.setDate(e.getDate() - 1);
    // If same day after subtraction, just show one date
    if (s.toDateString() === e.toDateString()) {
      return `${days[s.getDay()]} ${months[s.getMonth()]} ${s.getDate()}`;
    }
    // If end is before start (shouldn't happen but safety check)
    if (e < s) {
      return `${days[s.getDay()]} ${months[s.getMonth()]} ${s.getDate()}`;
    }
    return `${days[s.getDay()]} ${months[s.getMonth()]} ${s.getDate()} — ${days[e.getDay()]} ${months[e.getMonth()]} ${e.getDate()}`;
  };

  const BetCard = ({ bet }) => {
    const hidden = bet.selection === "???";
    const mktColor = m => m==="spreads"?"#4d9fff":m==="totals"?"#f5b731":m==="parlay"?"#ff4d6d":"#3d9e7a";
    return (
      <div style={{ backgroundColor:"#1a2235", borderRadius:8, padding:"10px 12px", marginBottom:6, minHeight:68, display:"flex", alignItems:"center", borderLeft:`3px solid ${hidden?"#374151":mktColor(bet.market)}` }}>
        {hidden ? (
          <div style={{ display:"flex", alignItems:"center", gap:8, width:"100%" }}>
            <span style={{ fontSize:18 }}>🔒</span>
            <div>
              <div style={{ fontSize:12, color:"#6b7280", fontWeight:600 }}>Hidden until kickoff</div>
              <div style={{ fontSize:10, color:"#4b5563" }}>Reveals when game starts</div>
            </div>
          </div>
        ) : (
          <div style={{ width:"100%" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#ffffff" }}>
                {bet.is_parlay
                  ? `⚡ ${bet.parlay_legs?.length || ""}-Leg Parlay`
                  : `${bet.selection}${bet.point ? ` ${bet.point>0?"+":""}${bet.point}` : ""}`
                }
              </div>
              <div style={{ fontSize:13, fontWeight:700, color:bet.result==="won"?"#3d9e7a":bet.result==="lost"?"#ef4444":"#f5b731", fontFamily:"'Inter',sans-serif" }}>
                {bet.result==="won"?`+${fmtNum(bet.to_win)}`:bet.result==="lost"?`-${fmtNum(bet.wager)}`:"⏳"}
              </div>
            </div>
            {bet.is_parlay ? (
              <div style={{ marginBottom:4 }}>
                {bet.parlay_legs?.map((l,i) => (
                  <div key={i} style={{ fontSize:11, color:"rgba(255,255,255,0.6)", marginBottom:2 }}>
                    <span style={{ color:"#ffffff", fontWeight:600 }}>{l.selection}{l.point?` ${l.point>0?"+":""}${l.point}`:""}</span>
                    <span style={{ color:"rgba(255,255,255,0.4)" }}> · {l.gameName||l.game_name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginBottom:4 }}>{bet.game_name}</div>
            )}
            <div style={{ display:"flex", gap:6 }}>
              <span style={{ fontSize:9, padding:"2px 6px", borderRadius:4, backgroundColor:`${mktColor(bet.market)}20`, color:mktColor(bet.market), fontWeight:600 }}>{bet.market_label}</span>
              <span style={{ fontSize:10, color:"rgba(255,255,255,0.4)" }}>{bet.price>0?`+${bet.price}`:bet.price} · {fmtNum(bet.wager)}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!selectedPeriod) return <div style={{ textAlign:"center", padding:60, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>No active period yet. The league may not have started.</div>;

  const completedPeriods = (allPeriods || []).filter(p => p.status === "completed");
  const sorted = [...matchups].sort((a,b) => {
    const aMe = a.team1_id===teamId||a.team2_id===teamId;
    const bMe = b.team1_id===teamId||b.team2_id===teamId;
    return aMe?-1:bMe?1:0;
  });

  return (
    <div style={{ padding:"16px 20px", maxWidth:760, margin:"0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom:16, padding:"14px 20px", backgroundColor:"rgba(29,78,216,0.08)", border:"1px solid rgba(29,78,216,0.2)", borderRadius:12, textAlign:"center" }}>
        <div style={{ fontFamily:"'Inter',sans-serif", fontSize:20, fontWeight:800, color:"#f0f4ff", marginBottom:4 }}>
          {selectedPeriod.period_type==="playoff" ? `🏆 Playoff Round ${selectedPeriod.playoff_round}` : `⚔️ Week ${selectedPeriod.period_number} Matchups`}
        </div>
        <div style={{ fontSize:12, color:"#6b7280", fontFamily:"'Inter',sans-serif", marginBottom:completedPeriods.length>0?10:0 }}>
          {formatDateRange(selectedPeriod.start_date, selectedPeriod.end_date)} · Starting credits: {fmtNum(sc)}
        </div>
        {completedPeriods.length > 0 && (
          <select onChange={e => {
            const pid = e.target.value;
            if (pid === "current") { setSelectedPeriodId(periodId); setSelectedPeriod(period); }
            else {
              const p = (allPeriods||[]).find(x=>x.id===pid);
              setSelectedPeriodId(pid);
              setSelectedPeriod(p);
            }
          }} style={{ marginTop:4, padding:"5px 12px", borderRadius:8, border:"1px solid rgba(255,255,255,0.15)", backgroundColor:"#1a2235", color:"#f0f4ff", fontFamily:"'Inter',sans-serif", fontSize:12, cursor:"pointer" }}>
            <option value="current" style={{ backgroundColor:"#1a2235", color:"#f0f4ff" }}>Week {period?.period_number} (Current)</option>
            {completedPeriods.sort((a,b)=>b.period_number-a.period_number).map(p=>(
              <option key={p.id} value={p.id} style={{ backgroundColor:"#1a2235", color:"#f0f4ff" }}>Week {p.period_number}</option>
            ))}
          </select>
        )}
      </div>

      {loading ? <div style={{ textAlign:"center", padding:40, color:"#6b7280" }}>Loading...</div> : sorted.map(m => {
        const isMyMatchup = m.team1_id===teamId||m.team2_id===teamId;
        const isExp = expanded.has(m.id);
        const t1cr = m.team1_credits ?? sc;
        const t2cr = m.team2_credits ?? sc;
        const total = t1cr + t2cr;
        const t1pct = total > 0 ? Math.round((t1cr/total)*100) : 50;
        const t2pct = 100 - t1pct;
        const diff = Math.abs(t1cr - t2cr);
        const myCredits = m.team1_id===teamId ? t1cr : t2cr;
        const oppCredits = m.team1_id===teamId ? t2cr : t1cr;
        const iAmAhead = myCredits >= oppCredits;
        const isCompleted = m.status === "completed";

        return (
          <div key={m.id} style={{ backgroundColor:"#ffffff", border:isMyMatchup?"1.5px solid #a7f3d0":"1px solid #f3f4f6", borderRadius:14, marginBottom:12, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.08)" }}>
            <div style={{ padding:"14px 16px" }}>
              {isMyMatchup && <div style={{ marginBottom:10 }}>
                <span style={{ fontSize:9, padding:"2px 8px", backgroundColor:"#f0fdf8", color:"#3d9e7a", border:"1px solid #a7f3d0", borderRadius:10, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>YOUR MATCHUP</span>
              </div>}

              {/* Teams */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 32px 1fr", alignItems:"start", gap:6, marginBottom:12 }}>
                {/* Team 1 */}
                <div style={{ minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:2 }}>
                    <span style={{ fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:700, color:"#1a1a2e", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.team1_name}</span>
                    {m.team1_id===teamId && <span style={{ fontSize:8, padding:"1px 4px", backgroundColor:"#f0fdf8", color:"#3d9e7a", border:"1px solid #a7f3d0", borderRadius:3, fontWeight:700, flexShrink:0 }}>YOU</span>}
                  </div>
                  <div style={{ fontSize:11, color:"#6b7280", marginBottom:6, whiteSpace:"nowrap" }}>{getRecord(m.team1_id, true)}</div>
                  <div style={{ fontFamily:"'Inter',sans-serif", fontSize:24, fontWeight:800, color:isCompleted?(m.winner_id===m.team1_id?"#3d9e7a":"#ef4444"):creditColor(t1cr), lineHeight:1, whiteSpace:"nowrap" }}>
                    {isCompleted ? fmtNum(m.team1_credits_end||sc) : fmtNum(t1cr)}
                    {isCompleted && m.winner_id===m.team1_id && <span style={{ fontSize:14, marginLeft:4 }}>✓</span>}
                  </div>
                  <div style={{ fontSize:10, color:"#9ca3af", marginTop:2, whiteSpace:"nowrap" }}>Week {selectedPeriod.period_number} Credits</div>
                  {!isCompleted && m.team1_bets?.length>0 && <div style={{ fontSize:10, color:"#9ca3af", marginTop:3, whiteSpace:"nowrap" }}>{m.team1_bets.length} bet{m.team1_bets.length!==1?"s":""} · {m.team1_bets.filter(b=>b.status==="pending"||b.selection==="???").length} pending</div>}
                </div>
                {/* VS */}
                <div style={{ fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:600, color:"#d1d5db", textAlign:"center", paddingTop:12 }}>VS</div>
                {/* Team 2 */}
                <div style={{ textAlign:"right", minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:4, justifyContent:"flex-end", marginBottom:2 }}>
                    {m.team2_id===teamId && <span style={{ fontSize:8, padding:"1px 4px", backgroundColor:"#f0fdf8", color:"#3d9e7a", border:"1px solid #a7f3d0", borderRadius:3, fontWeight:700, flexShrink:0 }}>YOU</span>}
                    <span style={{ fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:700, color:"#1a1a2e", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.team2_name}</span>
                  </div>
                  <div style={{ fontSize:11, color:"#6b7280", marginBottom:6, whiteSpace:"nowrap" }}>{getRecord(m.team2_id, true)}</div>
                  <div style={{ fontFamily:"'Inter',sans-serif", fontSize:24, fontWeight:800, color:isCompleted?(m.winner_id===m.team2_id?"#3d9e7a":"#ef4444"):creditColor(t2cr), lineHeight:1, whiteSpace:"nowrap" }}>
                    {isCompleted ? fmtNum(m.team2_credits_end||sc) : fmtNum(t2cr)}
                    {isCompleted && m.winner_id===m.team2_id && <span style={{ fontSize:14, marginLeft:4 }}>✓</span>}
                  </div>
                  <div style={{ fontSize:10, color:"#9ca3af", marginTop:2, whiteSpace:"nowrap" }}>Week {selectedPeriod.period_number} Credits</div>
                  {!isCompleted && m.team2_bets?.length>0 && <div style={{ fontSize:10, color:"#9ca3af", marginTop:3, whiteSpace:"nowrap" }}>{m.team2_bets.length} bet{m.team2_bets.length!==1?"s":""} · {m.team2_bets.filter(b=>b.status==="pending"||b.selection==="???").length} pending</div>}
                </div>
              </div>

              {/* Lead banner - my matchup only */}
              {isMyMatchup && !isCompleted && diff > 0 && (
                <div style={{ padding:"8px 12px", borderRadius:8, marginBottom:10, backgroundColor:iAmAhead?"#f0fdf8":"#fff5f5", border:`1px solid ${iAmAhead?"#a7f3d0":"#fecaca"}`, textAlign:"center", fontSize:13, fontWeight:600, color:iAmAhead?"#065f46":"#dc2626" }}>
                  {iAmAhead ? `🟢 Leading by ${fmtNum(diff)} credits` : `🔴 Trailing by ${fmtNum(diff)} credits`}
                </div>
              )}
              {isMyMatchup && isCompleted && (
                <div style={{ padding:"8px 12px", borderRadius:8, marginBottom:10, backgroundColor: (m.winner_id===teamId)?"#f0fdf8":"#fff5f5", border:`1px solid ${(m.winner_id===teamId)?"#a7f3d0":"#fecaca"}`, textAlign:"center", fontSize:13, fontWeight:700, color:(m.winner_id===teamId)?"#065f46":"#dc2626" }}>
                  {m.winner_id===teamId ? "🏆 You won this week!" : "😔 You lost this week"}
                </div>
              )}

              {/* Win probability bar */}
              <div>
                <div style={{ height:5, backgroundColor:"#f3f4f6", borderRadius:3, overflow:"hidden", marginBottom:4 }}>
                  <div style={{ height:"100%", width:`${t1pct}%`, background:"linear-gradient(to right,#3d9e7a,#1d4ed8)", borderRadius:3 }}/>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontSize:10, color:t1cr>=t2cr?"#3d9e7a":"#dc2626", fontWeight:600 }}>{m.team1_name.split(" ")[0]} {t1pct}%</span>
                  <span style={{ fontSize:10, color:t2cr>t1cr?"#3d9e7a":"#dc2626", fontWeight:600 }}>{t2pct}% {m.team2_name.split(" ")[0]}</span>
                </div>
              </div>
            </div>

            {/* Toggle bets */}
            <div style={{ borderTop:"1px solid #f3f4f6" }}>
              <button onClick={()=>toggleExpanded(m.id)} style={{ width:"100%", padding:"10px 16px", background:"transparent", border:"none", cursor:"pointer", fontSize:12, color:"#6b7280", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                {isExp ? "Hide bets ↑" : "View bets ↓"}
              </button>
            </div>

            {/* Expanded bets */}
            {isExp && (
              <div style={{ borderTop:"1px solid #f3f4f6", padding:"14px 16px", backgroundColor:"#fafafa" }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  {[{name:m.team1_name, bets:m.team1_bets, id:m.team1_id},{name:m.team2_name, bets:m.team2_bets, id:m.team2_id}].map(side=>(
                    <div key={side.id}>
                      <div style={{ fontSize:10, fontWeight:700, color:"#6b7280", letterSpacing:"0.06em", marginBottom:8, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {(side.name.length > 10 ? side.name.substring(0,10)+"..." : side.name).toUpperCase()}{side.id===teamId?" (YOU)":""}
                      </div>
                      {!side.bets||side.bets.length===0 ? (
                        <div style={{ backgroundColor:"#1a2235", borderRadius:8, padding:"10px 12px", minHeight:68, display:"flex", alignItems:"center" }}>
                          <span style={{ fontSize:12, color:"#4b5563", fontStyle:"italic" }}>No bets yet</span>
                        </div>
                      ) : side.bets.map((b,i)=><BetCard key={i} bet={b}/>)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── MY BETS VIEW ──────────────────────────────────────────────────────────────
function MyBetsView({ leagueId, teamId, allPeriods }) {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedParlays, setExpandedParlays] = useState(new Set());
  const [selectedWeek, setSelectedWeek] = useState("current");

  useEffect(() => {
    setLoading(true);
    // Fetch all bets across all periods
    fetch(`${API}/h2h/${leagueId}/my-bets-all/${teamId}`)
      .then(r => r.ok ? r.json() : [])
      .then(d => { setBets(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [leagueId, teamId]);

  const completedPeriods = (allPeriods||[]).filter(p=>p.status==="completed");
  const currentPeriod = (allPeriods||[]).find(p=>p.status==="active");

  const filteredBets = selectedWeek === "all" ? bets :
    selectedWeek === "current" ? bets.filter(b => {
      if (!currentPeriod) return false;
      return b.period_id === currentPeriod.id;
    }) : bets.filter(b => b.period_id === selectedWeek);

  const pending = bets.filter(b=>b.status==="pending");
  const settled = bets.filter(b=>b.status==="settled");

  const toggleParlay = (id) => setExpandedParlays(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const mktColor = m => m==="spreads"?"#4d9fff":m==="totals"?"#f5b731":m==="parlay"?"#D4537E":"#3d9e7a";

  const BetRow = ({ bet, isLast }) => {
    const won = bet.result==="won", lost = bet.result==="lost";
    const isParlay = bet.is_parlay || bet.market==="parlay";
    const isExpanded = expandedParlays.has(bet.id);
    const borderColor = mktColor(bet.market);

    return (
      <div style={{ borderTop: isLast===false?"0.5px solid #f3f4f6":"none" }}>
        <div style={{ borderLeft:`3px solid ${borderColor}`, padding:"12px 14px", display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:14, fontWeight:600, color:"#1a1a2e", marginBottom:2 }}>
              {isParlay ? `⚡ ${bet.parlay_legs?.length||""}${bet.parlay_legs?.length?"-Leg ":""}Parlay` : `${bet.selection}${bet.point?` ${bet.point>0?"+":""}${bet.point}`:""}`}
            </div>
            <div style={{ fontSize:12, color:"#9ca3af" }}>
              {isParlay ? bet.parlay_legs?.map(l=>l.selection).join(" · ") : `${bet.game_name} · ${bet.market_label} · ${bet.price>0?"+":""}${bet.price}`}
            </div>
          </div>
          <div style={{ textAlign:"right", flexShrink:0 }}>
            {bet.status==="pending" ? <>
              <div style={{ fontSize:13, fontWeight:600, color:"#1a1a2e" }}>{fmtNum(bet.wager)}</div>
              <div style={{ fontSize:11, color:"#3d9e7a" }}>+{fmtNum(bet.to_win)} to win</div>
            </> : <>
              <div style={{ fontSize:14, fontWeight:700, color:won?"#3d9e7a":lost?"#dc2626":"#6b7280" }}>
                {won?`+${fmtNum(bet.to_win)}`:lost?`-${fmtNum(bet.wager)}`:"Push"}
              </div>
            </>}
          </div>
          <span style={{ fontSize:10, padding:"3px 8px", borderRadius:20, fontWeight:600, flexShrink:0,
            backgroundColor:bet.status==="pending"?"#fffbeb":won?"#f0fdf8":lost?"#fff5f5":"#fffbeb",
            color:bet.status==="pending"?"#92400e":won?"#065f46":lost?"#dc2626":"#92400e",
            border:bet.status==="pending"?"1px solid #fcd34d":won?"1px solid #a7f3d0":lost?"1px solid #fecaca":"1px solid #fcd34d"
          }}>{bet.status==="pending"?"Pending":won?"Won":lost?"Lost":"Push"}</span>
        </div>
        {isParlay && (
          <div style={{ borderLeft:`3px solid ${borderColor}`, paddingLeft:14, paddingRight:14, paddingBottom:8 }}>
            <button onClick={()=>toggleParlay(bet.id)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:"#6b7280", padding:"4px 0", display:"flex", alignItems:"center", gap:4 }}>
              <span style={{ fontSize:11, color:"#9ca3af" }}>{bet.price>0?"+":""}${bet.price} odds</span>
              <span style={{ marginLeft:"auto", color:"#3d9e7a" }}>{isExpanded?"Hide legs ↑":"Show legs ↓"}</span>
            </button>
            {isExpanded && bet.parlay_legs?.map((leg,i) => {
              const lr = leg.result;
              const dr = lr || (bet.result==="won" ? "won" : null);
              const bc = dr==="won" ? "#3d9e7a" : dr==="lost" ? "#dc2626" : mktColor(leg.market);
              return (
                <div key={i} style={{ borderLeft:`3px solid ${bc}`, padding:"8px 10px", marginBottom:4, backgroundColor:"#f9fafb", borderRadius:"0 4px 4px 0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:"#1a1a2e" }}>{leg.selection}{leg.point?` ${leg.point>0?"+":""}${leg.point}`:""}</div>
                    <div style={{ fontSize:11, color:"#9ca3af" }}>{leg.gameName || leg.game_name} · {leg.price>0?"+":""}{leg.price}</div>
                  </div>
                  <span style={{ fontSize:16, fontWeight:700, color: dr==="won"?"#3d9e7a":dr==="lost"?"#dc2626":"#9ca3af" }}>{dr==="won"?"✓":dr==="lost"?"✗":"⏳"}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div style={{ textAlign:"center", padding:60, color:"#6b7280" }}>Loading bets...</div>;

  return (
    <div style={{ padding:"16px 20px", maxWidth:860, margin:"0 auto" }}>
      {/* Week filter */}
      <div style={{ marginBottom:16, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
        {[
          { value:"current", label:"This Week" },
          { value:"all", label:"All Time" },
          ...completedPeriods.sort((a,b)=>b.period_number-a.period_number).map(p=>({ value:p.id, label:`Week ${p.period_number}` })),
        ].map(opt => (
          <button key={opt.value} onClick={()=>setSelectedWeek(opt.value)} style={{ padding:"5px 14px", borderRadius:20, border:`1.5px solid ${selectedWeek===opt.value?"#1d4ed8":"#e5e7eb"}`, backgroundColor:selectedWeek===opt.value?"#eff6ff":"#ffffff", color:selectedWeek===opt.value?"#1d4ed8":"#6b7280", fontSize:12, fontWeight:selectedWeek===opt.value?700:400, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            {opt.label}
          </button>
        ))}
      </div>

      <div style={{ marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, padding:"10px 14px", backgroundColor:"#fffbeb", border:"1px solid #fcd34d", borderRadius:10 }}>
          <div style={{ fontFamily:"'Inter',sans-serif", fontSize:16, fontWeight:700, color:"#92400e" }}>⏳ Pending Bets</div>
          <span style={{ backgroundColor:"#fcd34d", color:"#92400e", fontSize:12, fontWeight:700, padding:"2px 8px", borderRadius:20 }}>{pending.length}</span>
        </div>
        {pending.length===0
          ? <div style={{ textAlign:"center", padding:20, backgroundColor:"#ffffff", borderRadius:12, border:"1px solid #f3f4f6", color:"#9ca3af", fontSize:13 }}>No pending bets this period</div>
          : <div style={{ backgroundColor:"#ffffff", borderRadius:12, border:"1px solid #f3f4f6", overflow:"hidden" }}>
              {pending.map((b,i) => <BetRow key={b.id} bet={b} isLast={i===0}/>)}
            </div>
        }
      </div>
      <div>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, padding:"10px 14px", backgroundColor:"#f3f4f6", border:"1px solid #e5e7eb", borderRadius:10 }}>
          <div style={{ fontFamily:"'Inter',sans-serif", fontSize:16, fontWeight:700, color:"#374151" }}>📋 Bet History</div>
          <span style={{ backgroundColor:"#e5e7eb", color:"#374151", fontSize:12, fontWeight:700, padding:"2px 8px", borderRadius:20 }}>{settled.length}</span>
        </div>
        {settled.length===0
          ? <div style={{ textAlign:"center", padding:20, backgroundColor:"#ffffff", borderRadius:12, border:"1px solid #f3f4f6", color:"#9ca3af", fontSize:13 }}>No settled bets yet</div>
          : <div style={{ backgroundColor:"#ffffff", borderRadius:12, border:"1px solid #f3f4f6", overflow:"hidden" }}>
              {settled.map((b,i) => <BetRow key={b.id} bet={b} isLast={i===0}/>)}
            </div>
        }
      </div>
    </div>
  );
}

// ─── PLAYOFF BRACKET VIEW ─────────────────────────────────────────────────────
function PlayoffBracketView({ leagueId, teamId, allPeriods, settings }) {
  const [bracketData, setBracketData] = useState([]);
  const [loading, setLoading] = useState(true);

  const playoffPeriods = (allPeriods||[]).filter(p => p.period_type === "playoff").sort((a,b) => a.period_number - b.period_number);

  useEffect(() => {
    if (playoffPeriods.length === 0) { setLoading(false); return; }
    Promise.all(
      playoffPeriods.map(p => fetch(`${API}/h2h/${leagueId}/matchups/${p.id}`).then(r=>r.ok?r.json():[]))
    ).then(results => {
      setBracketData(results);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [leagueId, allPeriods?.length]);

  if (loading) return <div style={{ textAlign:"center", padding:60, color:"#6b7280" }}>Loading bracket...</div>;

  if (playoffPeriods.length === 0) {
    const regularDone = (allPeriods||[]).filter(p=>p.period_type==="regular" && p.status==="completed").length;
    const totalRegular = settings?.total_regular_periods || 0;
    return (
      <div style={{ textAlign:"center", padding:60, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>
        <div style={{ fontSize:32, marginBottom:12 }}>🏆</div>
        <div style={{ fontFamily:"'Inter',sans-serif", fontSize:16, fontWeight:600, color:"#f0f4ff", marginBottom:8 }}>Playoffs haven't started yet</div>
        {settings?.playoff_teams > 0
          ? <div style={{ fontSize:13 }}>{regularDone} of {totalRegular} regular season weeks complete<br/>Top {settings.playoff_teams} teams advance to playoffs</div>
          : <div style={{ fontSize:13 }}>This league has no playoffs configured</div>
        }
      </div>
    );
  }

  const MatchupCard = ({ matchup, isMe }) => {
    if (!matchup) return null;
    const t1Won = matchup.winner_id === matchup.team1_id;
    const t2Won = matchup.winner_id === matchup.team2_id;
    const isComplete = matchup.status === "completed";
    const isBye = matchup.is_bye;

    if (isBye) return (
      <div style={{ backgroundColor:"#f0fdf8", border:"1.5px solid #a7f3d0", borderRadius:10, padding:"10px 14px", marginBottom:6, textAlign:"center" }}>
        <div style={{ fontSize:12, color:"#3d9e7a", fontWeight:600 }}>{matchup.team1_name}</div>
        <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>BYE — Advances automatically</div>
      </div>
    );

    return (
      <div style={{ backgroundColor:"#ffffff", border:isMe?"1.5px solid #a7f3d0":"1px solid #f3f4f6", borderRadius:10, overflow:"hidden", marginBottom:6, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
        {[
          { name:matchup.team1_name, id:matchup.team1_id, credits:matchup.team1_credits_end, won:t1Won },
          { name:matchup.team2_name, id:matchup.team2_id, credits:matchup.team2_credits_end, won:t2Won },
        ].map((team, i) => (
          <div key={i} style={{ padding:"8px 12px", display:"flex", alignItems:"center", justifyContent:"space-between", borderTop: i===0?"none":"1px solid #f3f4f6", backgroundColor: isComplete && team.won ? "#f0fdf8" : "transparent", opacity: isComplete && !team.won ? 0.5 : 1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              {isComplete && team.won && <span style={{ fontSize:12 }}>🏆</span>}
              <span style={{ fontSize:13, fontWeight:isComplete&&team.won?700:500, color:"#1a1a2e", fontFamily:"'Inter',sans-serif" }}>{team.name}</span>
              {team.id===teamId && <span style={{ fontSize:8, padding:"1px 4px", backgroundColor:"#f0fdf8", color:"#3d9e7a", border:"1px solid #a7f3d0", borderRadius:3, fontWeight:700 }}>YOU</span>}
            </div>
            {isComplete && team.credits != null && (
              <span style={{ fontFamily:"'Inter',sans-serif", fontSize:15, fontWeight:800, color:team.won?"#3d9e7a":"#ef4444" }}>{fmtNum(team.credits)}</span>
            )}
            {!isComplete && <span style={{ fontSize:11, color:"#9ca3af" }}>TBD</span>}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding:"16px 20px", maxWidth:760, margin:"0 auto" }}>
      <div style={{ marginBottom:16, padding:"12px 16px", backgroundColor:"rgba(29,78,216,0.08)", border:"1px solid rgba(29,78,216,0.2)", borderRadius:12, textAlign:"center" }}>
        <div style={{ fontFamily:"'Inter',sans-serif", fontSize:20, fontWeight:800, color:"#f0f4ff" }}>🏆 Playoff Bracket</div>
        <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>{settings?.playoff_teams} teams · {playoffPeriods.length} rounds</div>
      </div>

      {playoffPeriods.map((period, roundIdx) => {
        const matchups = bracketData[roundIdx] || [];
        const roundNum = period.playoff_round || roundIdx + 1;
        const isFinal = roundIdx === playoffPeriods.length - 1;
        const isMe = matchups.some(m => m.team1_id===teamId || m.team2_id===teamId);

        return (
          <div key={period.id} style={{ marginBottom:20 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <div style={{ height:1, flex:1, backgroundColor:"rgba(255,255,255,0.08)" }}/>
              <span style={{ fontSize:11, fontWeight:700, color: isFinal?"#f5b731":"#60a5fa", letterSpacing:"0.08em", padding:"3px 12px", borderRadius:20, border:`1px solid ${isFinal?"rgba(245,183,49,0.3)":"rgba(96,165,250,0.3)"}`, backgroundColor:isFinal?"rgba(245,183,49,0.1)":"rgba(29,78,216,0.1)" }}>
                {isFinal ? "🏆 CHAMPIONSHIP" : `ROUND ${roundNum}`}
              </span>
              <div style={{ height:1, flex:1, backgroundColor:"rgba(255,255,255,0.08)" }}/>
            </div>
            <div style={{ fontSize:11, color:"#6b7280", textAlign:"center", marginBottom:10, fontFamily:"'Inter',sans-serif" }}>
              {(() => {
                const s = new Date(period.start_date);
                const e = new Date(period.end_date);
                e.setDate(e.getDate()-1);
                const opts = {month:"short",day:"numeric"};
                if(s.toDateString()===e.toDateString()) return s.toLocaleDateString("en-US",opts);
                return `${s.toLocaleDateString("en-US",opts)} — ${e.toLocaleDateString("en-US",opts)}`;
              })()}
            </div>
            <div style={{ display:"grid", gridTemplateColumns: matchups.length > 2 ? "1fr 1fr" : "1fr", gap:10 }}>
              {matchups.map(m => (
                <MatchupCard key={m.id} matchup={m} isMe={m.team1_id===teamId||m.team2_id===teamId}/>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── BEST OF WEEK VIEW ────────────────────────────────────────────────────────
function BestOfWeekView({ leagueId, teamId, period }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!period?.id) { setLoading(false); return; }
    fetch(`${API}/h2h/${leagueId}/best-of-week/${period.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [leagueId, period]);

  if (loading) return <div style={{ textAlign:"center", padding:60, color:"#6b7280" }}>Loading...</div>;
  if (!period) return <div style={{ textAlign:"center", padding:60, color:"#6b7280" }}>No active week yet</div>;

  const StatCard = ({ emoji, title, value, sub, color="#1d4ed8" }) => (
    <div style={{ backgroundColor:"#ffffff", border:"1px solid #f3f4f6", borderRadius:14, padding:"20px 16px", textAlign:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.08)" }}>
      <div style={{ fontSize:32, marginBottom:8 }}>{emoji}</div>
      <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", letterSpacing:"0.08em", marginBottom:6, fontFamily:"'Inter',sans-serif" }}>{title}</div>
      <div style={{ fontFamily:"'Inter',sans-serif", fontSize:22, fontWeight:800, color, lineHeight:1, marginBottom:4 }}>{value || "—"}</div>
      {sub && <div style={{ fontSize:12, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding:"16px 20px", maxWidth:760, margin:"0 auto" }}>
      <div style={{ marginBottom:20, padding:"12px 16px", backgroundColor:"rgba(29,78,216,0.08)", border:"1px solid rgba(29,78,216,0.2)", borderRadius:12, textAlign:"center" }}>
        <div style={{ fontFamily:"'Inter',sans-serif", fontSize:18, fontWeight:800, color:"#f0f4ff" }}>🔥 Best of Week {period?.period_number}</div>
        <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>Highlights from this week's action</div>
      </div>

      {!stats || Object.keys(stats).length === 0 ? (
        <div style={{ textAlign:"center", padding:40, backgroundColor:"#ffffff", borderRadius:14, border:"1px solid #f3f4f6", color:"#9ca3af" }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🎲</div>
          <div style={{ fontSize:14, fontWeight:600 }}>No bets settled yet this week</div>
          <div style={{ fontSize:12, marginTop:6 }}>Check back after games complete!</div>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <StatCard emoji="🎯" title="HIGHEST ODDS WIN" value={stats.highest_odds_win?.odds ? `+${stats.highest_odds_win.odds}` : null} sub={stats.highest_odds_win ? `${stats.highest_odds_win.team_name} · ${stats.highest_odds_win.selection}` : null} color="#3d9e7a"/>
          <StatCard emoji="💰" title="BIGGEST WIN" value={stats.biggest_win ? `+${fmtNum(stats.biggest_win.amount)}` : null} sub={stats.biggest_win?.team_name} color="#3d9e7a"/>
          <StatCard emoji="💸" title="BIGGEST LOSS" value={stats.biggest_loss ? `-${fmtNum(stats.biggest_loss.amount)}` : null} sub={stats.biggest_loss?.team_name} color="#dc2626"/>
          <StatCard emoji="🎲" title="BIGGEST GAMBLER" value={stats.most_wagered ? fmtNum(stats.most_wagered.total) : null} sub={stats.most_wagered ? `${stats.most_wagered.team_name} wagered` : null} color="#1d4ed8"/>
          <StatCard emoji="⚡" title="BIGGEST PARLAY WIN" value={stats.biggest_parlay ? `+${fmtNum(stats.biggest_parlay.amount)}` : null} sub={stats.biggest_parlay ? `${stats.biggest_parlay.team_name} · ${stats.biggest_parlay.legs}-leg` : null} color="#D4537E"/>
          <StatCard emoji="🔥" title="MOST BETS" value={stats.most_bets?.count} sub={stats.most_bets?.team_name} color="#f5b731"/>
        </div>
      )}
    </div>
  );
}

// ─── LEAGUE INFO VIEW ──────────────────────────────────────────────────────────
function LeagueInfoView({ leagueId, settings, allPeriods, period }) {
  const ALL_SPORTS = {
    "americanfootball_nfl": "🏈 NFL",
    "basketball_nba": "🏀 NBA",
    "baseball_mlb": "⚾ MLB",
    "icehockey_nhl": "🏒 NHL",
    "americanfootball_ncaaf": "🏈 NCAAF",
    "basketball_ncaab": "🏀 NCAAB",
    "soccer_epl": "⚽ EPL",
    "soccer_uefa_champs_league": "⚽ UCL",
    "soccer_usa_mls": "⚽ MLS",
    "golf_pga_tour": "⛳ PGA Tour",
    "golf_masters_tournament_winner": "⛳ Masters",
    "mma_mixed_martial_arts": "🥊 MMA",
    "boxing_boxing": "🥊 Boxing",
  };

  const days = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

  const regularPeriods = allPeriods.filter(p => p.period_type === "regular");
  const playoffPeriods = allPeriods.filter(p => p.period_type === "playoff");
  const completedCount = allPeriods.filter(p => p.status === "completed").length;

  const formatDate = (d, isEnd=false) => {
    const dt = new Date(d);
    if (isEnd) dt.setDate(dt.getDate() - 1);
    return dt.toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" });
  };

  const InfoRow = ({ label, value }) => (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid #f3f4f6" }}>
      <span style={{ fontSize:13, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:600, color:"#1a1a2e", fontFamily:"'Inter',sans-serif" }}>{value}</span>
    </div>
  );

  return (
    <div style={{ padding:"16px 20px", maxWidth:760, margin:"0 auto" }}>
      {/* League settings */}
      <div style={{ backgroundColor:"#ffffff", borderRadius:14, border:"1px solid #f3f4f6", padding:"16px 20px", marginBottom:16, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontFamily:"'Inter',sans-serif", fontSize:16, fontWeight:700, color:"#1a1a2e", marginBottom:12 }}>⚙️ League Settings</div>
        <InfoRow label="Week length" value={`${settings?.period_length_days || "?"} days`}/>
        <InfoRow label="Starting credits per matchup" value={fmtNum(settings?.starting_credits || 0)}/>

        <InfoRow label="Regular season weeks" value={settings?.total_regular_periods || "?"}/>
        <InfoRow label="Playoff teams" value={settings?.playoff_teams > 0 ? settings.playoff_teams : "No playoffs"}/>
        {settings?.playoff_teams > 0 && <InfoRow label="Weeks per playoff round" value={settings?.playoff_rounds_per_period || 1}/>}
        <InfoRow label="Total weeks" value={allPeriods.length}/>
        <div style={{ paddingTop:10 }}>
          <div style={{ fontSize:13, color:"#6b7280", fontFamily:"'Inter',sans-serif", marginBottom:8 }}>Sports available</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {(settings?.sports || []).map(s => (
              <span key={s} style={{ fontSize:12, padding:"3px 10px", borderRadius:20, backgroundColor:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe", fontFamily:"'Inter',sans-serif", fontWeight:500 }}>
                {ALL_SPORTS[s] || s}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Season progress */}
      <div style={{ backgroundColor:"#ffffff", borderRadius:14, border:"1px solid #f3f4f6", padding:"16px 20px", marginBottom:16, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontFamily:"'Inter',sans-serif", fontSize:16, fontWeight:700, color:"#1a1a2e", marginBottom:12 }}>📅 Season Schedule</div>
        <div style={{ marginBottom:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
            <span style={{ fontSize:12, color:"#6b7280" }}>{completedCount} of {allPeriods.length} weeks complete</span>
            <span style={{ fontSize:12, color:"#1d4ed8", fontWeight:600 }}>{Math.round((completedCount/allPeriods.length)*100)}%</span>
          </div>
          <div style={{ height:6, backgroundColor:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${(completedCount/allPeriods.length)*100}%`, backgroundColor:"#1d4ed8", borderRadius:3 }}/>
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:300, overflowY:"auto" }}>
          {allPeriods.map(p => {
            const isCurrent = p.id === period?.id;
            const isComplete = p.status === "completed";
            const isPlayoff = p.period_type === "playoff";
            return (
              <div key={p.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderRadius:8, backgroundColor:isCurrent?"#eff6ff":isComplete?"#f9fafb":"#ffffff", border:isCurrent?"1px solid #bfdbfe":"1px solid #f3f4f6" }}>
                <span style={{ fontSize:10, padding:"2px 7px", borderRadius:10, fontWeight:700, fontFamily:"'Inter',sans-serif", backgroundColor:isPlayoff?"#fffbeb":isComplete?"#f0fdf8":isCurrent?"#eff6ff":"#f3f4f6", color:isPlayoff?"#92400e":isComplete?"#3d9e7a":isCurrent?"#1d4ed8":"#9ca3af", flexShrink:0 }}>
                  {isPlayoff?`PLAYOFF ${p.playoff_round}`:`WK ${p.period_number}`}
                </span>
                <span style={{ fontSize:12, color:"#6b7280", fontFamily:"'Inter',sans-serif", flex:1 }}>
                  {formatDate(p.start_date) === formatDate(p.end_date, true) 
                    ? formatDate(p.start_date)
                    : `${formatDate(p.start_date)} — ${formatDate(p.end_date, true)}`}
                </span>
                <span style={{ fontSize:11, color:isComplete?"#3d9e7a":isCurrent?"#1d4ed8":"#9ca3af", fontWeight:600 }}>
                  {isComplete?"✓ Done":isCurrent?"▶ Now":"Upcoming"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── CHAMPION SCREEN ──────────────────────────────────────────────────────────
function ChampionScreen({ leagueId, teamId, leagueName, onBack }) {
  const [champion, setChampion] = useState(null);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/h2h/${leagueId}/standings`)
      .then(r=>r.json())
      .then(d => {
        setStandings(d);
        const champ = d.find(s => s.is_champion);
        setChampion(champ || d[0]);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [leagueId]);

  const isMe = champion?.team_id === teamId;

  if (loading) return (
    <div style={{ height:"100vh", backgroundColor:"#0d1b2a", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ fontSize:32, color:"#f5b731" }}>🏆</div>
    </div>
  );

  return (
    <div style={{ height:"100vh", overflow:"hidden", backgroundColor:"#0d1b2a", display:"flex", flexDirection:"column", fontFamily:"'Inter',sans-serif" }}>
      <style>{FONT}{CSS}</style>

      {/* Top bar */}
      <div style={{ backgroundColor:"#111827", borderBottom:"1px solid rgba(255,255,255,0.07)", padding:"12px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:"#ffffff", cursor:"pointer", fontSize:22, fontWeight:900, padding:0 }}>⟵</button>
        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:16, fontWeight:800, color:"#f0f4ff" }}>{leagueName}</div>
        <div style={{ width:32 }}/>
      </div>

      <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 20px", textAlign:"center" }}>
        {/* Trophy animation */}
        <div style={{ fontSize:80, marginBottom:16, animation:"pulse 2s infinite" }}>🏆</div>

        {isMe ? (
          <>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:38, fontWeight:900, color:"#f5b731", marginBottom:8 }}>You Won! 🏆</div>
            <div style={{ fontSize:18, color:"#f0f4ff", marginBottom:6, fontWeight:600 }}>Congratulations {champion?.team_name}!</div>
            <div style={{ fontSize:14, color:"#9ca3af", marginBottom:32 }}>You are the Scraps champion of {leagueName}</div>
          </>
        ) : (
          <>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:32, fontWeight:900, color:"#f5b731", marginBottom:8 }}>League Complete</div>
            <div style={{ fontSize:18, color:"#f0f4ff", marginBottom:6, fontWeight:600 }}>🏆 {champion?.team_name} wins!</div>
            <div style={{ fontSize:14, color:"#9ca3af", marginBottom:32 }}>Champion of {leagueName}</div>
          </>
        )}

        {/* Final standings */}
        <div style={{ width:"100%", maxWidth:480, backgroundColor:"#111827", borderRadius:16, border:"1px solid rgba(255,255,255,0.08)", overflow:"hidden" }}>
          <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.06)", fontFamily:"'Inter',sans-serif", fontSize:16, fontWeight:700, color:"#f5b731" }}>
            Final Standings
          </div>
          {standings.map((s, i) => {
            const isChamp = s.is_champion;
            const isMyRow = s.team_id === teamId;
            return (
              <div key={s.team_id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 20px", borderBottom:"1px solid rgba(255,255,255,0.05)", backgroundColor: isMyRow ? "rgba(245,183,49,0.05)" : "transparent" }}>
                <div style={{ fontSize:20, width:28, textAlign:"center" }}>
                  {isChamp ? "🏆" : i===1 ? "🥈" : i===2 ? "🥉" : <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:16, color:"#6b7280" }}>#{i+1}</span>}
                </div>
                <div style={{ flex:1, textAlign:"left" }}>
                  <div style={{ fontSize:14, fontWeight:600, color: isChamp ? "#f5b731" : "#f0f4ff" }}>
                    {s.team_name}
                    {isMyRow && <span style={{ fontSize:9, padding:"1px 5px", backgroundColor:"rgba(245,183,49,0.2)", color:"#f5b731", border:"1px solid rgba(245,183,49,0.3)", borderRadius:4, fontWeight:700, marginLeft:6 }}>YOU</span>}
                  </div>
                  <div style={{ fontSize:12, color:"#6b7280" }}>{s.owner_name}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, fontWeight:800, color:"#3d9e7a" }}>{s.wins}W-{s.losses}L</div>
                  <div style={{ fontSize:11, color:"#6b7280" }}>{fmtNum(s.total_credits_gained)} gained</div>
                </div>
              </div>
            );
          })}
        </div>

        <button onClick={onBack} style={{ marginTop:28, padding:"12px 32px", backgroundColor:"#1d4ed8", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
          Back to My Leagues
        </button>
      </div>
    </div>
  );
}

// ─── MAIN H2H LEAGUE ──────────────────────────────────────────────────────────
function AdminBanner({ leagueId, isMobile }) {
  const key = `scraps_admin_seen_${leagueId}`;
  const [seen, setSeen] = useState(()=>!!localStorage.getItem(key));
  if (seen) return null;
  return (
    <div style={{background:"#1e3a5f",borderBottom:"2px solid #3b82f6",padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexShrink:0}}>
      <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
        <span style={{fontSize:isMobile?16:18,flexShrink:0}}>⚙️</span>
        <span style={{fontSize:isMobile?12:13,color:"#bfdbfe",fontFamily:"'Inter',sans-serif",lineHeight:1.4}}>
          <strong style={{color:"#ffffff"}}>You're the league admin.</strong>{" "}
          {isMobile ? "Tap Admin in the nav below to manage your league." : "Tap ⚙️ next to the league name to manage settings, members and more."}
        </span>
      </div>
      <button onClick={()=>{localStorage.setItem(key,"1");setSeen(true);}} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:6,color:"#ffffff",cursor:"pointer",fontSize:12,fontWeight:600,padding:"5px 12px",fontFamily:"'Inter',sans-serif",flexShrink:0,whiteSpace:"nowrap"}}>
        Got it
      </button>
    </div>
  );
}

export default function H2HLeague({ teamId, leagueId, teamName, leagueName, onBack, emailVerified, email, isAdmin, onAdminClick }) {
  const [leagueStatus, setLeagueStatus] = useState(null);
  const [activeView, setActiveView] = useState("book");
  const [slip, setSlip] = useState([]);
  const [blockedMsg, setBlockedMsg] = useState("");
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSport, setActiveSport] = useState(null); // set from league settings
  const [credits, setCredits] = useState(0);
  const [period, setPeriod] = useState(null);
  const [leagueSettings, setLeagueSettings] = useState(null);
  const [allPeriods, setAllPeriods] = useState([]);
  const [leaveConfirm, setLeaveConfirm] = useState(false);
  const [oddsError, setOddsError] = useState(false);
  const [sportDropdownOpen, setSportDropdownOpen] = useState(false);
  const [mobileSlipOpen, setMobileSlipOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const ALL_SPORTS = [
    { key:"americanfootball_nfl", label:"NFL", emoji:"🏈" },
    { key:"basketball_nba", label:"NBA", emoji:"🏀" },
    { key:"baseball_mlb", label:"MLB", emoji:"⚾" },
    { key:"icehockey_nhl", label:"NHL", emoji:"🏒" },
    { key:"americanfootball_ncaaf", label:"NCAAF", emoji:"🏈" },
    { key:"basketball_ncaab", label:"NCAAB", emoji:"🏀" },
    { key:"soccer_epl", label:"EPL", emoji:"⚽" },
    { key:"soccer_uefa_champs_league", label:"UCL", emoji:"⚽" },
    { key:"soccer_usa_mls", label:"MLS", emoji:"⚽" },
  ];

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (activeSport && period) loadOdds(); }, [activeSport, period?.id]);

  const loadData = async () => {
    try {
      const [teamRes, settingsRes, periodRes, scheduleRes, leagueRes] = await Promise.all([
        fetch(`${API}/team/${teamId}`),
        fetch(`${API}/h2h/${leagueId}/settings`),
        fetch(`${API}/h2h/${leagueId}/current-period`),
        fetch(`${API}/h2h/${leagueId}/schedule`),
        fetch(`${API}/league/${leagueId}`),
      ]);
      const team = await teamRes.json();
      const settings = await settingsRes.json();
      const currentPeriod = await periodRes.json();
      const schedule = await scheduleRes.json();
      setCredits(team.credits || 0);
      setLeagueSettings(settings);
      setPeriod(currentPeriod);
      setAllPeriods(schedule || []);
      const leagueData = await leagueRes.json();
      setLeagueStatus(leagueData?.status);
      // Set default sport from league settings
      if (settings?.sports?.length > 0) {
        setActiveSport(s => s || settings.sports[0]);
      }
      if (settings?.sports?.length > 0) setActiveSport(settings.sports[0]);

      // Get pending bets count
      if (currentPeriod) {
        const bets = await fetch(`${API}/h2h/${leagueId}/my-bets/${teamId}`).then(r=>r.json());
        setPendingCount(bets.filter(b=>b.status==="pending").length);
      }
    } catch(e) { console.error(e); }
  };

  const loadOdds = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/odds/${activeSport}`);
      if (!res.ok) throw new Error("Failed");
      let allGames = await res.json();
      // Tag each game with the sport key it was fetched under — the raw Odds
      // API response has no "sport" field, and settlement needs this exact
      // key (e.g. "americanfootball_ncaaf") to look up scores later. Without
      // this, every H2H bet is saved with sport:"" and can never auto-settle.
      allGames = allGames.map(g => ({ ...g, sport: activeSport }));
      // Filter to only show games that start before the period ends
      if (period?.end_date) {
        const periodEnd = new Date(period.end_date);
        allGames = allGames.filter(g => new Date(g.commence_time) < periodEnd);
      }
      setGames(allGames);
      setOddsError(false);
    } catch(e) {
      setOddsError(true);
      setGames([]);
    }
    setLoading(false);
  };

  const handleAddBet = useCallback((bet, gameId, market) => {
    if (!bet) { setSlip(s => s.filter(b => !(b.gameId === gameId && b.market === market))); return; }
    setSlip(s => {
      // Block combining moneyline + spread on the same game. Other market
      // pairings on the same game (e.g. spread + total) are allowed as
      // separate legs; picking the SAME market on the same game again just
      // replaces the existing leg (normal toggle behavior).
      const conflict = s.find(b => b.gameId === gameId && b.market !== bet.market &&
        ((b.market === "spreads" && bet.market === "h2h") || (b.market === "h2h" && bet.market === "spreads")));
      if (conflict) {
        setBlockedMsg("Can't combine spread and moneyline on the same game");
        return s;
      }
      return [...s.filter(b => !(b.gameId === gameId && b.market === bet.market)), bet];
    });
  }, []);

  const handleRemove = useCallback((gameId, market) => setSlip(s => s.filter(b => !(b.gameId === gameId && b.market === market))), []);

  const handleBetPlaced = () => {
    setSlip([]);
    loadData();
  };

  useEffect(() => {
    if (!blockedMsg) return;
    const t = setTimeout(() => setBlockedMsg(""), 2600);
    return () => clearTimeout(t);
  }, [blockedMsg]);

  const leagueSports = leagueSettings?.sports
    ? ALL_SPORTS.filter(s => leagueSettings.sports.includes(s.key))
    : ALL_SPORTS.slice(0, 4);

  if (leagueStatus === "completed") return (
    <ChampionScreen leagueId={leagueId} teamId={teamId} leagueName={leagueName} onBack={onBack}/>
  );

  return (
    <div style={{ height:"100vh", overflow:"hidden", backgroundColor:"#0a0e1a", display:"flex", flexDirection:"column", fontFamily:"'Inter',sans-serif" }}>
      <style>{FONT}{CSS}</style>

      {blockedMsg && (
        <div style={{ position:"fixed", top:16, left:"50%", transform:"translateX(-50%)", zIndex:9999, background:"#1a2235", color:"#fff", padding:"10px 18px", borderRadius:8, fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:600, boxShadow:"0 4px 16px rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)" }}>{blockedMsg}</div>
      )}

      {leaveConfirm && (
        <div onClick={()=>setLeaveConfirm(false)} style={{ position:"fixed", inset:0, backgroundColor:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:20 }}>
          <div onClick={e=>e.stopPropagation()} style={{ backgroundColor:"#ffffff", borderRadius:16, padding:28, maxWidth:380, width:"100%", textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>👋</div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:800, color:"#1a1a2e", marginBottom:8 }}>Leave League?</div>
            <div style={{ fontSize:14, color:"#374151", marginBottom:24, fontFamily:"'Inter',sans-serif" }}>You'll return to your leagues hub. You can come back anytime.</div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={onBack} style={{ flex:1, padding:13, backgroundColor:"#3d9e7a", color:"#fff", border:"none", borderRadius:8, fontFamily:"'Inter',sans-serif", fontSize:15, fontWeight:600, cursor:"pointer" }}>Leave</button>
              <button onClick={()=>setLeaveConfirm(false)} style={{ flex:1, padding:13, backgroundColor:"transparent", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:8, cursor:"pointer", fontSize:14, fontFamily:"'Inter',sans-serif" }}>Stay</button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div style={{ backgroundColor:"#111827", borderBottom:"1px solid rgba(255,255,255,0.07)", padding:isMobile?"10px 14px":"12px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:isMobile?10:16, minWidth:0 }}>
          <button onClick={()=>setLeaveConfirm(true)} style={{ background:"none", border:"none", color:"#ffffff", cursor:"pointer", fontSize:isMobile?26:13, fontFamily:"'Inter',sans-serif", flexShrink:0, padding:0, lineHeight:1, fontWeight:900 }}>
            {isMobile ? "≡" : "≡ My Leagues"}
          </button>
          {!isMobile && <div style={{ width:1, height:20, backgroundColor:"rgba(255,255,255,0.1)" }}/>}
          <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:isMobile?20:18, fontWeight:800, color:"#f0f4ff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{leagueName}</div>
            {!isMobile && <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, backgroundColor:"rgba(29,78,216,0.15)", color:"#60a5fa", fontFamily:"'Inter',sans-serif", fontWeight:600, border:"1px solid rgba(29,78,216,0.3)" }}>⚔️ H2H</span>}
            {isAdmin && !isMobile && <button onClick={onAdminClick} style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:6, padding:"3px 8px", cursor:"pointer", fontSize:14, color:"#9ca3af", flexShrink:0 }} title="League Admin">⚙️</button>}
          </div>
        </div>
        <div style={{ flexShrink:0, display:"flex", alignItems:"center", gap:8 }}>
          {!isMobile && <div style={{ textAlign:"right" }}><div style={{ fontSize:11, color:"#6b7280", fontFamily:"'Inter',sans-serif", marginBottom:1 }}>{teamName}</div></div>}
          <div style={{ backgroundColor:"rgba(29,78,216,0.2)", border:"1.5px solid #1d4ed8", borderRadius:10, padding:isMobile?"6px 12px":"6px 14px" }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:isMobile?18:22, fontWeight:800, color:"#ffffff", lineHeight:1, textAlign:"center" }}>{fmtNum(credits)}</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.6)", textAlign:"center", marginTop:1 }}>{period ? `Week ${period.period_number}` : "credits"}</div>
          </div>
        </div>
      </div>

      {/* Period info bar */}
      {period && (
        <div style={{ backgroundColor:"#111827", borderBottom:"1px solid rgba(255,255,255,0.07)", padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"center", gap:12, flexShrink:0, flexWrap:"wrap" }}>
          <span style={{ background:"rgba(29,78,216,0.2)", border:"1px solid #1d4ed8", borderRadius:20, padding:"3px 12px", fontSize:11, fontWeight:700, color:"#60a5fa", letterSpacing:"0.06em", flexShrink:0 }}>
            {period.period_type==="playoff"?"PLAYOFF":"WEEK"} {period.period_number}
          </span>
          <span style={{ fontSize:13, color:"#f0f4ff", fontWeight:500, fontFamily:"'Inter',sans-serif", flexShrink:0 }}>
            {(()=>{ const s=new Date(period.start_date); const e=new Date(period.end_date); e.setDate(e.getDate()-1); const d=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"]; const m=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]; if(s.toDateString()===e.toDateString()) return `${d[s.getDay()]} ${m[s.getMonth()]} ${s.getDate()}`; return `${d[s.getDay()]} ${m[s.getMonth()]} ${s.getDate()} — ${d[e.getDay()]} ${m[e.getMonth()]} ${e.getDate()}`; })()}
          </span>
          <span style={{ fontSize:11, color:"#6b7280", fontFamily:"'Inter',sans-serif", flexShrink:0 }}>
            {allPeriods.filter(p=>p.status==="completed").length} of {leagueSettings?.total_regular_periods||"?"} complete
          </span>
          {oddsError && <span style={{ fontSize:11, color:"#f5b731" }}>⚠ Sample odds</span>}
        </div>
      )}

      {/* Betting not open yet banner */}
      {period && period.status === "upcoming" && (
        <div style={{ backgroundColor:"#eff6ff", borderBottom:"1px solid #bfdbfe", padding:"10px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <div style={{ fontSize:13, color:"#1e40af", fontFamily:"'Inter',sans-serif" }}>
            🔒 <strong>Betting opens {new Date(period.start_date).toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" })}</strong> — View your Week {period.period_number} matchup in the Matchups tab
          </div>
        </div>
      )}

      {/* Email verification banner */}
      {isAdmin && <AdminBanner leagueId={leagueId} isMobile={isMobile} />}

      {!emailVerified && (
        <div style={{ backgroundColor:"#fffbeb", borderBottom:"2px solid #f59e0b", padding:"12px 20px", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
            <div style={{ fontSize:13, color:"#92400e", fontFamily:"'Inter',sans-serif", fontWeight:600 }}>📧 Please verify your email to place bets</div>
            <button onClick={async()=>{ await fetch(`${API}/auth/resend-verification?email=${encodeURIComponent(email)}`,{method:"POST"}); alert("Verification email sent to " + email + "!"); }} style={{ padding:"6px 14px", background:"#f59e0b", border:"none", borderRadius:6, color:"#fff", cursor:"pointer", fontSize:12, fontFamily:"'Inter',sans-serif", fontWeight:700, whiteSpace:"nowrap", flexShrink:0 }}>Resend Email</button>
          </div>
          <div style={{ fontSize:12, color:"#92400e", fontFamily:"'Inter',sans-serif", marginTop:4 }}>
            We sent a verification link to <strong>{email}</strong>. Check your inbox (and spam folder). Click the link in that email to activate your account.
          </div>
        </div>
      )}

      {/* Nav tabs */}
      {isMobile ? (
        <>
          {/* Mobile - primary tabs + More button */}
          <div style={{ backgroundColor:"#111827", borderBottom:"1px solid rgba(255,255,255,0.07)", display:"flex", gap:0, justifyContent:"center", width:"100%" }}>
            {[
              { key:"book", label:"Sportsbook", icon:"🏈" },
              { key:"matchups", label:"Matchups", icon:"⚔️" },
              { key:"standings", label:"Standings", icon:"🏆" },
              { key:"bets", label:"My Bets", icon:"🎟", badge:pendingCount },
              ...(isAdmin ? [{ key:"admin", label:"Admin", icon:"⚙️" }] : [{ key:"more", label:"More", icon:"···" }]),
            ].map(tab => (
              <button key={tab.key} onClick={()=>{ if(tab.key==="admin") { onAdminClick(); } else if(tab.key==="more") { setMoreMenuOpen(!moreMenuOpen); } else { setActiveView(tab.key); setMoreMenuOpen(false); } }} style={{
                flex:1, padding:"10px 4px", border:"none", backgroundColor:"transparent", cursor:"pointer",
                color: (tab.key==="more"&&(["best","bracket","info"].includes(activeView)||moreMenuOpen)) ? "#f0f4ff" : activeView===tab.key ? "#f0f4ff" : "#6b7280",
                borderBottom: (tab.key==="more"&&(["best","bracket","info"].includes(activeView)||moreMenuOpen)) ? "3px solid #1d4ed8" : activeView===tab.key ? "3px solid #1d4ed8" : "3px solid transparent",
                fontFamily:"'Inter',sans-serif", fontSize:11, fontWeight: activeView===tab.key ? 700 : 400,
                whiteSpace:"nowrap", display:"flex", flexDirection:"column", alignItems:"center", gap:2,
              }}>
                <span style={{ fontSize:18 }}>{tab.icon}</span>
                <span style={{ display:"flex", alignItems:"center", gap:3, justifyContent:"center" }}>
                  {tab.label === "My Bets" ? "My Bets" : tab.label.split(" ")[0]}
                  {tab.badge > 0 && <span style={{ backgroundColor:"#1d4ed8", color:"#fff", borderRadius:10, fontSize:9, fontWeight:700, padding:"1px 5px", lineHeight:1.4 }}>{tab.badge}</span>}
                </span>
              </button>
            ))}
          </div>
          {/* More menu dropdown */}
          {moreMenuOpen && (
            <div style={{ position:"fixed", inset:0, zIndex:200 }} onClick={()=>setMoreMenuOpen(false)}>
              <div onClick={e=>e.stopPropagation()} style={{ position:"absolute", bottom:0, left:0, right:0, backgroundColor:"#1a2235", borderRadius:"16px 16px 0 0", padding:"8px 0 20px", boxShadow:"0 -4px 20px rgba(0,0,0,0.4)" }}>
                <div style={{ width:36, height:4, backgroundColor:"rgba(255,255,255,0.15)", borderRadius:2, margin:"8px auto 16px" }}/>
                {[
                  { key:"best", label:"Best Bets", icon:"🔥", sub:"Highlights from this week" },
                  { key:"bracket", label:"Playoff Bracket", icon:"🏆", sub:"View the playoff picture" },
                  { key:"info", label:"League Info", icon:"ℹ️", sub:"Settings and schedule" },
                ].map(tab => (
                  <button key={tab.key} onClick={()=>{ setActiveView(tab.key); setMoreMenuOpen(false); }} style={{
                    width:"100%", padding:"14px 20px", border:"none", backgroundColor: activeView===tab.key ? "rgba(29,78,216,0.15)" : "transparent",
                    cursor:"pointer", display:"flex", alignItems:"center", gap:14, textAlign:"left",
                  }}>
                    <span style={{ fontSize:24 }}>{tab.icon}</span>
                    <div>
                      <div style={{ fontFamily:"'Inter',sans-serif", fontSize:15, fontWeight:600, color:"#f0f4ff" }}>{tab.label}</div>
                      <div style={{ fontFamily:"'Inter',sans-serif", fontSize:12, color:"#6b7280" }}>{tab.sub}</div>
                    </div>
                    {activeView===tab.key && <span style={{ marginLeft:"auto", color:"#1d4ed8", fontSize:16 }}>✓</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        /* Desktop - all tabs */
        <div style={{ backgroundColor:"#111827", borderBottom:"1px solid rgba(255,255,255,0.07)", padding:"0 24px", display:"flex", justifyContent:"center", flexShrink:0 }}>
          {[
            { key:"book", label:"Sportsbook", icon:"🏈" },
            { key:"matchups", label:"Matchups", icon:"⚔️" },
            { key:"standings", label:"Standings", icon:"🏆" },
            { key:"bets", label:"My Bets", icon:"🎟", badge:pendingCount },
            { key:"best", label:"Best Bets", icon:"🔥" },
            { key:"bracket", label:"Bracket", icon:"🏆" },
            { key:"info", label:"League Info", icon:"ℹ️" },
          ].map(tab => (
            <button key={tab.key} onClick={()=>setActiveView(tab.key)} style={{
              padding:"14px 20px", border:"none", backgroundColor:"transparent", cursor:"pointer",
              color: activeView===tab.key ? "#f0f4ff" : "#6b7280",
              borderBottom: activeView===tab.key ? "3px solid #1d4ed8" : "3px solid transparent",
              fontFamily:"'Inter',sans-serif", fontSize:14, fontWeight: activeView===tab.key ? 700 : 400,
              whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:8,
            }}>
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.badge > 0 && <span style={{ backgroundColor:"#1d4ed8", color:"#fff", borderRadius:10, fontSize:10, fontWeight:700, padding:"1px 5px" }}>{tab.badge}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Sport tabs - only on book */}
      {activeView === "book" && (
        <div style={{ backgroundColor:"#0f172a", borderBottom:"1px solid rgba(255,255,255,0.07)", flexShrink:0 }}>
          {isMobile ? (
            <>
              <div style={{ padding:"6px 12px", display:"flex", gap:4, alignItems:"center", background:"#0a0e1a", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                <span style={{ fontSize:9, color:"#6b7280", fontFamily:"'Inter',sans-serif", flexShrink:0, marginRight:2 }}>Types:</span>
                {(activeSport?.includes("golf") ? [{key:"h2h",label:"ML",color:"#3d9e7a"},{key:"parlay",label:"Parlay",color:"#ff4d6d"}] : [{key:"spreads",label:"Spread",color:"#4d9fff"},{key:"h2h",label:"ML",color:"#3d9e7a"},{key:"totals",label:"Total",color:"#f5b731"},{key:"parlay",label:"Parlay",color:"#ff4d6d"},{key:"teaser",label:"Teaser",color:"#1d4ed8"}]).map(t=>(
                  <span key={t.key} style={{ padding:"2px 7px", borderRadius:20, background:"#ffffff", border:"1px solid #e5e7eb", color:t.color, fontSize:9, fontFamily:"'Inter',sans-serif", fontWeight:600, whiteSpace:"nowrap", flexShrink:1 }}>{t.label}</span>
                ))}
              </div>
              <div style={{ padding:"6px 14px", position:"relative" }}>
                <button onClick={()=>setSportDropdownOpen(!sportDropdownOpen)} style={{ width:"100%", padding:"7px 14px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:20, color:"#f0f4ff", fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <span>{leagueSports.find(s=>s.key===activeSport)?.emoji} {leagueSports.find(s=>s.key===activeSport)?.label}</span>
                  <span style={{ fontSize:10, color:"#6b7280" }}>{sportDropdownOpen?"▲":"▼"}</span>
                </button>
                {sportDropdownOpen && (
                  <div style={{ position:"absolute", top:"calc(100% + 4px)", left:14, right:14, background:"#1a2235", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10, zIndex:50, overflow:"hidden" }}>
                    {leagueSports.map(s=>(
                      <button key={s.key} onClick={()=>{setActiveSport(s.key);setSportDropdownOpen(false);}} style={{ width:"100%", padding:"10px 14px", background:activeSport===s.key?"rgba(29,78,216,0.15)":"transparent", border:"none", color:activeSport===s.key?"#60a5fa":"#f0f4ff", fontFamily:"'Inter',sans-serif", fontSize:13, cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:8 }}>
                        <span>{s.emoji}</span>{s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div style={{ display:"flex", overflowX:"auto", padding:"0 20px", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                {leagueSports.map(s=>(
                  <button key={s.key} onClick={()=>setActiveSport(s.key)} style={{ display:"flex", alignItems:"center", gap:8, padding:"14px 20px", border:"none", cursor:"pointer", background:"transparent", color:activeSport===s.key?"#f0f4ff":"#6b7280", borderBottom:activeSport===s.key?"3px solid #1d4ed8":"3px solid transparent", fontFamily:"'Inter',sans-serif", fontSize:15, fontWeight:activeSport===s.key?700:400, whiteSpace:"nowrap" }}>
                    <span style={{ fontSize:16 }}>{s.emoji}</span> {s.label}
                  </button>
                ))}
              </div>
              <div style={{ padding:"8px 20px", display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ fontSize:11, color:"#4b5563", fontFamily:"'Inter',sans-serif", fontWeight:500, marginRight:4 }}>Bet types:</span>
                {(activeSport?.includes("golf") ? [{key:"h2h",label:"Moneyline",color:"#3d9e7a"},{key:"parlay",label:"Parlay",color:"#ff4d6d"}] : [{key:"spreads",label:"Spread",color:"#4d9fff"},{key:"h2h",label:"Moneyline",color:"#3d9e7a"},{key:"totals",label:"Total",color:"#f5b731"},{key:"parlay",label:"Parlay",color:"#ff4d6d"},{key:"teaser",label:"Teaser",color:"#1d4ed8"}]).map(t=>(
                  <span key={t.key} style={{ padding:"4px 12px", borderRadius:20, background:"#ffffff", border:"1px solid #e5e7eb", color:t.color, fontSize:12, fontFamily:"'Inter',sans-serif", fontWeight:600 }}>{t.label}</span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Content */}
      {activeView === "standings" && <div style={{ flex:1, overflowY:"auto" }}><StandingsView leagueId={leagueId} teamId={teamId}/></div>}
      {activeView === "matchups" && <div style={{ flex:1, overflowY:"auto" }}><MatchupsView leagueId={leagueId} teamId={teamId} periodId={period?.id} period={period} startingCredits={leagueSettings?.starting_credits} allPeriods={allPeriods}/></div>}
      {activeView === "bets" && <div style={{ flex:1, overflowY:"auto" }}><MyBetsView leagueId={leagueId} teamId={teamId} allPeriods={allPeriods}/></div>}
      {activeView === "best" && <div style={{ flex:1, overflowY:"auto" }}><BestOfWeekView leagueId={leagueId} teamId={teamId} period={period}/></div>}
      {activeView === "bracket" && <div style={{ flex:1, overflowY:"auto" }}><PlayoffBracketView leagueId={leagueId} teamId={teamId} allPeriods={allPeriods} settings={leagueSettings}/></div>}
      {activeView === "info" && <div style={{ flex:1, overflowY:"auto" }}><LeagueInfoView leagueId={leagueId} settings={leagueSettings} allPeriods={allPeriods} period={period}/></div>}

      {activeView === "book" && (
        <div style={{ display:"flex", flex:1, overflow:"hidden" }}>
          <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ flex:1, overflowY:"auto", padding:12 }}>
              {!period ? (
                <div style={{ textAlign:"center", padding:60, color:"#6b7280" }}>
                  <div style={{ fontSize:32, marginBottom:12 }}>⏳</div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, color:"#f0f4ff", marginBottom:8 }}>LEAGUE NOT STARTED YET</div>
                  <div style={{ fontSize:13, color:"#6b7280" }}>Waiting for the admin to start the league and set a start date</div>
                </div>
              ) : period && period.status === "upcoming" ? (
                <div style={{ textAlign:"center", padding:60, color:"#6b7280" }}>
                  <div style={{ fontSize:32, marginBottom:12 }}>🔒</div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, color:"#f0f4ff", marginBottom:8 }}>BETTING NOT OPEN YET</div>
                  <div style={{ fontSize:13, color:"#6b7280", marginBottom:8 }}>Week {period.period_number} opens on {new Date(period.start_date).toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric" })}</div>
                  <div style={{ fontSize:13, color:"#6b7280" }}>Check the Matchups tab to see who you're playing!</div>
                </div>
        ) : loading ? (
                <div style={{ textAlign:"center", padding:60, color:"#4b5563" }}>
                  <div style={{ fontSize:24, marginBottom:12, animation:"pulse 1.2s infinite" }}>⟳</div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif" }}>LOADING LINES...</div>
                </div>
              ) : games.length === 0 ? (
                <div style={{ textAlign:"center", padding:60, color:"#4b5563" }}>
                  <div style={{ fontSize:32, marginBottom:12 }}>🏟</div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:16, marginBottom:6 }}>NO GAMES SCHEDULED</div>
                  <div style={{ fontSize:13, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>No games within this matchup window</div>
                </div>
              ) : games.map(g => <GameCard key={g.id} game={{...g, sport:activeSport}} slip={slip} onAddBet={handleAddBet}/>)}
            </div>
          </div>

          {/* Mobile bet slip */}
          {isMobile && slip.length > 0 && (
            <div style={{ position:"fixed", bottom:0, left:0, right:0, zIndex:150 }}>
              {mobileSlipOpen && (
                <div style={{ position:"fixed", inset:0, backgroundColor:"rgba(0,0,0,0.5)" }} onClick={()=>setMobileSlipOpen(false)}>
                  <div onClick={e=>e.stopPropagation()} style={{ position:"absolute", bottom:0, left:0, right:0, backgroundColor:"#ffffff", borderRadius:"20px 20px 0 0", maxHeight:"80vh", display:"flex", flexDirection:"column", boxShadow:"0 -8px 40px rgba(0,0,0,0.4)" }}>
                    <div style={{ padding:"14px 20px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", backgroundColor:"#ffffff", borderRadius:"20px 20px 0 0", flexShrink:0 }}>
                      <div style={{ fontFamily:"'Inter',sans-serif", fontSize:16, fontWeight:600, color:"#1d4ed8" }}>🎟 Bet Slip</div>
                      <button onClick={()=>setMobileSlipOpen(false)} style={{ background:"none", border:"none", fontSize:20, color:"#9ca3af", cursor:"pointer" }}>✕</button>
                    </div>
                    <div style={{ overflowY:"auto", flex:1 }}>
                      <BetSlip slip={slip} onRemove={handleRemove} credits={credits} leagueId={leagueId} teamId={teamId} periodId={period?.id} matchupId={null} onBetPlaced={()=>{ handleBetPlaced(); setMobileSlipOpen(false); }} activeSport={activeSport}/>
                    </div>
                  </div>
                </div>
              )}
              {!mobileSlipOpen && (
                <div onClick={()=>setMobileSlipOpen(true)} style={{ backgroundColor:"#1d4ed8", padding:"12px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", boxShadow:"0 -4px 20px rgba(0,0,0,0.3)" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ backgroundColor:"rgba(255,255,255,0.25)", borderRadius:12, padding:"2px 10px", fontSize:13, fontWeight:700, color:"#fff" }}>{slip.length}</span>
                    <span style={{ fontFamily:"'Inter',sans-serif", fontSize:14, fontWeight:600, color:"#fff" }}>{slip.length===1?"1 bet selected":`${slip.length} bets — parlay`}</span>
                  </div>
                  <span style={{ fontFamily:"'Inter',sans-serif", fontSize:13, color:"rgba(255,255,255,0.8)" }}>View slip ↑</span>
                </div>
              )}
            </div>
          )}

          {/* Desktop bet slip - hidden on mobile */}
          {!isMobile && (
            <div style={{ width:320, borderLeft:"1px solid rgba(255,255,255,0.08)", backgroundColor:"#ffffff", display:"flex", flexDirection:"column", flexShrink:0, borderRadius:"12px", overflow:"hidden", alignSelf:"stretch" }}>
              <div style={{ padding:"16px 20px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", backgroundColor:"#ffffff", flexShrink:0 }}>
                <div style={{ fontFamily:"'Inter',sans-serif", fontSize:16, fontWeight:500, color:"#1d4ed8" }}>🎟 Bet Slip</div>
                {slip.length > 0 && (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ backgroundColor:"#1d4ed8", color:"#fff", fontSize:11, fontWeight:700, width:22, height:22, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{slip.length}</span>
                    <button onClick={()=>setSlip([])} style={{ background:"none", border:"none", color:"#9ca3af", fontSize:12, cursor:"pointer", fontFamily:"'Inter',sans-serif", fontWeight:500 }}>Clear</button>
                  </div>
                )}
              </div>
              <BetSlip slip={slip} onRemove={handleRemove} credits={credits} leagueId={leagueId} teamId={teamId} periodId={period?.id} matchupId={null} onBetPlaced={handleBetPlaced} activeSport={activeSport}/>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
